import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import TrackerShell from "../../components/tracker/TrackerShell";
import "../MyTeam/shared-tracker-styles.css";
import "./associate-tracker.css";
import { useToast } from "../../contexts/ToastContext";
import { db, auth } from "../../firebase";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  startAfter,
  limit,
  setDoc,
  runTransaction,
  getDoc,
  where,
} from "firebase/firestore";

import UserProfileCard from "../../components/profile/UserProfileCard";
import useProfileModalRoute from "../../components/profile/useProfileModalRoute";
import { addNote, deleteNote as deleteNoteFromService, subscribeToProspectNotes } from "../../services/notesService";

// Import UI 2.0 context and new tracker components
import { useUIVersion } from "../../contexts/UIVersionContext";
import { useAuth } from "../../hooks/useAuth";
import { ACCOUNT_TYPES, useAccountType } from "../../hooks/useAccountType";
import { useDebounce } from "../../hooks/useDebounce";
import {
  TrackerContainer,
  TrackerGrid,
  TrackerNotes,
  TrackerFilters,
  filterByDateRange,
  filterByBaseShop,
} from "../../components/tracker-ui";
import {
  IconUsers,
  IconTarget,
  IconBook,
  IconLicense,
  IconCheck,
} from "@tabler/icons-react";

/* =========================
   Data Sources & Field Mapping
========================= */
const DATA_SOURCES = {
  prospectsCollection: "prospects",
  productionCollection: "production",
  trackersCollection: "trackers",
};

const FIELD_MAP = {
  associateIdField: "recruiterId",
  associateNameField: "recruiter",
  productionOwnerIdField: "ownerId",
  productionPointsField: "points",
  licensingRecruiterField: "licensing.recruiter",
};

/* =========================
   Sync Constants & Utilities
========================= */
const SOURCE = {
  ASSOCIATE: "associate",
  FOUR_BY_FOUR: "fourByFour",
};

// Hierarchy Filtering
const DOWNLINE_PLANS = new Set([
  ACCOUNT_TYPES.LEADER,
  ACCOUNT_TYPES.BROKER,
  ACCOUNT_TYPES.SENIOR_BROKER,
  ACCOUNT_TYPES.ADMIN,
]);

function buildAccessibleUsers(plan, allUsers, currentUserId) {
  if (!currentUserId) return new Set();

  // New Agent / Agent: only their own data
  if (!DOWNLINE_PLANS.has(plan)) {
    return new Set([currentUserId]);
  }

  // Admin: can monitor everything
  if (plan === ACCOUNT_TYPES.ADMIN) {
    const all = new Set([currentUserId]);
    allUsers.forEach((u) => all.add(u.id));
    return all;
  }

  // Leaders/Brokers/Senior Brokers: their users + full downline (via recruiterId chain)
  const childrenMap = new Map();
  allUsers.forEach((u) => {
    if (!u?.recruiterId) return;
    if (!childrenMap.has(u.recruiterId)) childrenMap.set(u.recruiterId, []);
    childrenMap.get(u.recruiterId).push(u.id);
  });

  const allowed = new Set([currentUserId]);
  const stack = [currentUserId];
  while (stack.length) {
    const node = stack.pop();
    const kids = childrenMap.get(node) || [];
    kids.forEach((kid) => {
      if (!allowed.has(kid)) {
        allowed.add(kid);
        stack.push(kid);
      }
    });
  }

  return allowed;
}

// Field mapping between Associate and 4x4 fields within the same document
const FIELDS_MAP = [
  // Milestones mapping
  {
    associatePath: "associate.milestones.observe4Recruits",
    fourByFourPath: "fourByFour.milestones.observe4Recruits",
  },
  {
    associatePath: "associate.milestones.observe4Clients",
    fourByFourPath: "fourByFour.milestones.observe4Clients",
  },
  // Direct mirrors
  {
    associatePath: "associate.multiHanded",
    fourByFourPath: "fourByFour.finish1stRecruit",
  },
  {
    associatePath: "associate.tenThreResultsGoals",
    fourByFourPath: "fourByFour.savings1",
  },
  {
    associatePath: "associate.selfImprovement",
    fourByFourPath: "fourByFour.bigEvent", // ✅ FIXED: Use bigEvent, not eventReg
  },
  // Savings mapping
  {
    associatePath: "associate.savings.secondDone",
    fourByFourPath: "fourByFour.savings2",
  },
  {
    associatePath: "associate.savings.thirdDone",
    fourByFourPath: "fourByFour.savings3",
  },
  {
    associatePath: "associate.savings.fourthDone",
    fourByFourPath: "fourByFour.savings4",
  },
];

// UI key to associate path mapping
const UI_KEY_TO_ASSOCIATE_PATH = {
  obsRecruitsDone: "associate.milestones.observe4Recruits",
  obsClientsDone: "associate.milestones.observe4Clients",
  multiHanded: "associate.multiHanded",
  tenThreeRulesGoals: "associate.tenThreResultsGoals",
  selfImprovement: "associate.selfImprovement",
  registerConvention: "associate.registrationConvention",
  savingsSecondDone: "associate.savings.secondDone",
  savingsThirdDone: "associate.savings.thirdDone",
  savingsFourthDone: "associate.savings.fourthDone",
};

// util: find mapped peer path
function getPeerPath(source, path) {
  const item = FIELDS_MAP.find((m) =>
    source === SOURCE.ASSOCIATE
      ? m.associatePath === path
      : m.fourByFourPath === path
  );
  if (!item) return null;
  return source === SOURCE.ASSOCIATE ? item.fourByFourPath : item.associatePath;
}

// PATCH a single field + lastUpdatedBy
async function patchSingleField(userId, fieldPath, value, lastUpdatedBy) {
  const ref = doc(db, "trackers", userId);
  await updateDoc(ref, {
    [fieldPath]: value,
    lastUpdatedBy,
    updatedAt: serverTimestamp(),
  });
}

// Helper to read nested field value
function readPath(obj, path) {
  return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
}

// Recompute derived milestones based on current data
async function recomputeMilestones(userId, changedPath, changedValue) {
  const ref = doc(db, "trackers", userId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();

  // Helper to get value considering pending change
  const val = (p) => {
    if (p === changedPath) return changedValue;
    return !!readPath(data, p);
  };

  // 1) recruit2 AND recruit3 -> observe4Recruits
  // ✅ Use NEW field names from 4x4 tracker
  const r2 = val("fourByFour.finish2ndRecruit");
  const r3 = val("fourByFour.finish3rdRecruit");
  const observe4Recruits = !!(r2 && r3);

  // 2) savings2 AND savings3 AND savings4 -> observe4Clients
  // ✅ Use NEW field names from 4x4 tracker
  const s2 = val("associate.savings.secondDone") || val("fourByFour.finish2ndSavings");
  const s3 = val("associate.savings.thirdDone") || val("fourByFour.finish3rdSavings");
  const s4 = val("associate.savings.fourthDone") || val("fourByFour.finish4thSavings");
  const observe4Clients = !!(s2 && s3 && s4);

  // Only update if there are actual changes
  const currentObserve4Recruits = readPath(
    data,
    "associate.milestones.observe4Recruits"
  );
  const currentObserve4Clients = readPath(
    data,
    "associate.milestones.observe4Clients"
  );

  const updates = [];

  if (currentObserve4Recruits !== observe4Recruits) {
    updates.push(
      patchSingleField(
        userId,
        "associate.milestones.observe4Recruits",
        observe4Recruits,
        SOURCE.ASSOCIATE
      ),
      patchSingleField(
        userId,
        "fourByFour.milestones.observe4Recruits",
        observe4Recruits,
        SOURCE.ASSOCIATE
      )
    );
  }

  if (currentObserve4Clients !== observe4Clients) {
    updates.push(
      patchSingleField(
        userId,
        "associate.milestones.observe4Clients",
        observe4Clients,
        SOURCE.ASSOCIATE
      ),
      patchSingleField(
        userId,
        "fourByFour.milestones.observe4Clients",
        observe4Clients,
        SOURCE.ASSOCIATE
      )
    );
  }

  // Execute all updates in parallel
  if (updates.length > 0) {
    await Promise.all(updates);
  }
}

// Check if a path change should trigger milestone recomputation
function shouldRecomputeMilestones(path) {
  const milestoneTriggers = [
    // ✅ NEW field names from 4x4 tracker
    "fourByFour.finish2ndRecruit",
    "fourByFour.finish3rdRecruit",
    "fourByFour.finish2ndSavings",
    "fourByFour.finish3rdSavings",
    "fourByFour.finish4thSavings",
    "associate.savings.secondDone",
    "associate.savings.thirdDone",
    "associate.savings.fourthDone",
  ];

  return milestoneTriggers.includes(path);
}

// NEW: Auto-check Register for Convention when Self-Improvement is checked
async function handleSelfImprovementToggle(userId, value) {
  if (value) {
    // When Self-Improvement is checked, also check Register for Convention
    await patchSingleField(
      userId,
      "associate.registrationConvention",
      true,
      SOURCE.ASSOCIATE
    );
    // ✅ Note: fourByFour.bigEvent is already handled by the peer mapping above
    // No need to set it again here
  }
}

// Associate toggle handler with bi-directional sync and milestone recomputation
async function onAssociateToggle(userId, associateFieldPath, value) {
  // 1) Write the changed associate field
  await patchSingleField(userId, associateFieldPath, value, SOURCE.ASSOCIATE);

  // 2) Apply direct mirror to 4x4 field (with special one-way logic for selfImprovement → bigEvent)
  const peerPath = getPeerPath(SOURCE.ASSOCIATE, associateFieldPath);
  if (peerPath) {
    // Special case: selfImprovement → bigEvent is one-way (only when turning ON)
    if (associateFieldPath === "associate.selfImprovement" && value === true) {
      // Turn ON bigEvent when Self-Improvement is turned ON
      await patchSingleField(userId, peerPath, true, SOURCE.ASSOCIATE);
    } else if (associateFieldPath !== "associate.selfImprovement") {
      // For all other fields, apply normal bi-directional sync
      await patchSingleField(userId, peerPath, value, SOURCE.ASSOCIATE);
    }
    // Note: When selfImprovement is turned OFF, we don't sync to bigEvent (no action)
  }

  // 3) Special case: selfImprovement → associate.bigEvent (one-way when turning ON)
  if (associateFieldPath === "associate.selfImprovement" && value === true) {
    // Also turn ON associate.bigEvent when Self-Improvement is turned ON
    await patchSingleField(userId, "associate.bigEvent", true, SOURCE.ASSOCIATE);
  }

  // 4) Handle Self-Improvement → Register for Convention auto-check
  if (associateFieldPath === "associate.selfImprovement" && value) {
    await handleSelfImprovementToggle(userId, value);
  }

  // 5) Recompute derived milestones for relevant field changes
  if (shouldRecomputeMilestones(associateFieldPath)) {
    await recomputeMilestones(userId, associateFieldPath, value);
  }
}

/* =========================
   Firestore Refs & Constants
========================= */
const trackersRef = (userId) => doc(db, "trackers", userId);

/* =========================
   Helper functions
========================= */
const ensureAssociateExists = async (userId, rowData = {}) => {
  try {
    // First check if document exists
    const docRef = doc(db, "trackers", userId);
    const docSnap = await getDoc(docRef);

    // If document exists, return immediately - do NOT write defaults
    if (docSnap.exists()) {
      return;
    }

    // Only create minimal skeleton if document doesn't exist
    const minimalSkeleton = {
      associate: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        recruiter: rowData?.recruiter || "",
        email: rowData?.email || "",
        phone: rowData?.phone || "",
        leader: rowData?.leader || "",
        level: rowData?.level || "",
        code: rowData?.code || "",
        date: rowData?.date || null,
      },
      lastUpdatedBy: SOURCE.ASSOCIATE,
      updatedAt: serverTimestamp(),
    };

    await setDoc(docRef, minimalSkeleton, { merge: true });
  } catch (error) {
    console.error("Error ensuring associate exists:", error);
    throw error;
  }
};

/* =========================
   Helpers
========================= */
const fmtInt = (n) =>
  (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const rid = (r) => r.id;

const displayName = (u) => {
  const combo = [u.firstName, u.lastName].filter(Boolean).join(" ");
  return u.name || u.fullName || u["Full Name"] || combo || "—";
};

const initialsFrom = (name) => {
  const n = (name || "").trim();
  if (!n) return "?";
  const p = n.split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
};

function AvatarSquare({ row, size = 34, onClick }) {
  const url = row.photoURL || row.avatarUrl || row.photo;
  const txt = initialsFrom(displayName(row));
  return url ? (
    <img
      src={url}
      alt={displayName(row)}
      className="avatar-square"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        objectFit: "cover",
      }}
      onClick={onClick}
    />
  ) : (
    <div
      className="avatar-fallback"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        background: "rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        color: "#f3d67a",
        fontSize: "12px",
      }}
      onClick={onClick}
      title="No profile photo"
    >
      {txt}
    </div>
  );
}

function formatDateShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

/* =========================
   Data Watchers for Auto-calc Columns
========================= */
function useDataWatchers() {
  const watcherUnsubsRef = useRef(new Map());

  // Watch recruits count
  const watchRecruits = useCallback((userRow, setValue) => {
    const userId = userRow.id;
    const userName = userRow.name;
    const userRecruiterName = userRow.recruiter || userRow.name;

    // We need to query BOTH:
    // 1. New prospects with recruiterId (ID-based)
    // 2. Old prospects with recruiter name (name-based)

    const recruitsById = query(
      collection(db, DATA_SOURCES.prospectsCollection),
      where(FIELD_MAP.associateIdField, "==", userId)
    );

    const recruitsByName = query(
      collection(db, DATA_SOURCES.prospectsCollection),
      where(FIELD_MAP.associateNameField, "==", userName)
    );

    const allRecruits = new Map(); // Use Map to deduplicate by ID

    const updateCount = () => {
      const count = allRecruits.size;
      const recruits = Array.from(allRecruits.values());

      setValue(count);

      // Update the associate document
      patchSingleField(
        userId,
        "associate.recruitTtl",
        count,
        SOURCE.ASSOCIATE
      );
    };

    // Listen to both queries
    const unsub1 = onSnapshot(
      recruitsById,
      (snapshot) => {
        snapshot.docs.forEach(doc => {
          allRecruits.set(doc.id, { id: doc.id, ...doc.data() });
        });
        updateCount();
      },
      (error) => {
        console.error(`❌ Error watching recruits by ID for ${userName}:`, error);
      }
    );

    const unsub2 = onSnapshot(
      recruitsByName,
      (snapshot) => {
        snapshot.docs.forEach(doc => {
          allRecruits.set(doc.id, { id: doc.id, ...doc.data() });
        });
        updateCount();
      },
      (error) => {
        console.error(`❌ Error watching recruits by name for ${userName}:`, error);
      }
    );

    // Return combined unsubscribe function
    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  // Watch production points sum
  const watchProductionSum = useCallback((userRow, setValue) => {
    const userId = userRow.id;

    const q = query(
      collection(db, DATA_SOURCES.productionCollection),
      where(FIELD_MAP.productionOwnerIdField, "==", userId)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        let totalPoints = 0;
        snapshot.forEach((doc) => {
          const data = doc.data();
          const points = data[FIELD_MAP.productionPointsField] || 0;
          totalPoints += points;
        });
        setValue(totalPoints);

        // Update the associate document
        patchSingleField(
          userId,
          "associate.personalPoints",
          totalPoints,
          SOURCE.ASSOCIATE
        );
      },
      (error) => {
        console.error("Error watching production:", error);
      }
    );

    return unsub;
  }, []);

  // Watch licenses count
  const watchLicenses = useCallback((userRow, setValue) => {
    const userId = userRow.id;
    const userRecruiterName = userRow.recruiter || userRow.name;

    const unsub = onSnapshot(
      collection(db, DATA_SOURCES.trackersCollection),
      (snapshot) => {
        let licenseCount = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const licensingRecruiter = readPath(
            data,
            FIELD_MAP.licensingRecruiterField
          );

          // Match by ID or name
          if (
            licensingRecruiter === userId ||
            licensingRecruiter === userRecruiterName
          ) {
            licenseCount++;
          }
        });

        setValue(licenseCount);

        // Update the associate document
        patchSingleField(
          userId,
          "associate.licensesInTtl",
          licenseCount,
          SOURCE.ASSOCIATE
        );
      },
      (error) => {
        console.error("Error watching licenses:", error);
      }
    );

    return unsub;
  }, []);

  // Start all watchers for a row
  const startWatchersForRow = useCallback(
    (row, updateRowData) => {
      const userId = row.id;

      // Clean up existing watchers
      if (watcherUnsubsRef.current.has(userId)) {
        const unsubs = watcherUnsubsRef.current.get(userId);
        unsubs.forEach((unsub) => unsub());
        watcherUnsubsRef.current.delete(userId);
      }

      const unsubs = [];

      // Start recruits watcher
      unsubs.push(
        watchRecruits(row, (count) => {
          updateRowData(userId, { recruitsTtl: count });
        })
      );

      // Start production watcher
      unsubs.push(
        watchProductionSum(row, (points) => {
          updateRowData(userId, { personalPoints: points });
        })
      );

      // Start licenses watcher
      unsubs.push(
        watchLicenses(row, (count) => {
          updateRowData(userId, { licensesTtl: count });
        })
      );

      watcherUnsubsRef.current.set(userId, unsubs);
    },
    [watchRecruits, watchProductionSum, watchLicenses]
  );

  // Clean up watchers
  const cleanupWatchers = useCallback(() => {
    for (const [userId, unsubs] of watcherUnsubsRef.current) {
      unsubs.forEach((unsub) => unsub());
    }
    watcherUnsubsRef.current.clear();
  }, []);

  return {
    startWatchersForRow,
    cleanupWatchers,
  };
}

/* =========================
   Modal Components for Drill-down Trackers
========================= */

// Recruits Modal
function RecruitsModal({ isOpen, onClose, row }) {
  const [recruits, setRecruits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen || !row) return;

    setLoading(true);

    // Query by BOTH recruiterId (new) AND recruiter name (old)
    const recruitsById = query(
      collection(db, DATA_SOURCES.prospectsCollection),
      where(FIELD_MAP.associateIdField, "==", row.id)
    );

    const recruitsByName = query(
      collection(db, DATA_SOURCES.prospectsCollection),
      where(FIELD_MAP.associateNameField, "==", row.name)
    );

    const allRecruits = new Map();

    const updateRecruits = () => {
      const recruitsList = Array.from(allRecruits.values());
      setRecruits(recruitsList);
      setLoading(false);
    };

    // Listen to both queries
    const unsub1 = onSnapshot(
      recruitsById,
      (snapshot) => {
        snapshot.docs.forEach(doc => {
          allRecruits.set(doc.id, { id: doc.id, ...doc.data() });
        });
        updateRecruits();
      },
      (error) => {
        console.error("Error loading recruits by ID:", error);
        setLoading(false);
      }
    );

    const unsub2 = onSnapshot(
      recruitsByName,
      (snapshot) => {
        snapshot.docs.forEach(doc => {
          allRecruits.set(doc.id, { id: doc.id, ...doc.data() });
        });
        updateRecruits();
      },
      (error) => {
        console.error("Error loading recruits by name:", error);
        setLoading(false);
      }
    );

    // Return combined unsubscribe function
    return () => {
      unsub1();
      unsub2();
    };
  }, [isOpen, row]);

  const filteredRecruits = useMemo(() => {
    if (!searchTerm.trim()) return recruits;
    const term = searchTerm.toLowerCase();
    return recruits.filter(
      (recruit) =>
        (recruit.name || "").toLowerCase().includes(term) ||
        (recruit.email || "").toLowerCase().includes(term) ||
        (recruit.phone || "").toLowerCase().includes(term)
    );
  }, [recruits, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="tracker-modal-backdrop" onClick={onClose}>
      <div className="tracker-modal tracker-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="tracker-modal__head">
          <h3>
            {recruits.length} Recruits - {row.name}
          </h3>
          <button className="tracker-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="tracker-modal__body">
          <div className="modal-search-container">
            <input
              type="text"
              placeholder="Search recruits by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modal-search-input"
            />
          </div>
          <div className="modal-table-container">
            {loading ? (
              <div className="modal-loading">Loading recruits...</div>
            ) : filteredRecruits.length === 0 ? (
              <div className="modal-empty">
                {searchTerm
                  ? "No matching recruits found"
                  : "No recruits found"}
              </div>
            ) : (
              <table className="modal-prospects-table">
                <thead>
                  <tr>
                    <th style={{ width: "180px" }}>Name</th>
                    <th style={{ width: "180px" }}>Email</th>
                    <th style={{ width: "130px" }}>Phone</th>
                    <th style={{ width: "100px" }}>Status</th>
                    <th style={{ width: "80px" }}>Level</th>
                    <th style={{ width: "120px" }}>Leader</th>
                    <th style={{ width: "100px" }}>Agency Code</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecruits.map((recruit) => (
                    <tr key={recruit.id}>
                      <td>
                        <div className="recruit-name-cell">
                          <div className="recruit-avatar">
                            {recruit.name?.charAt(0)?.toUpperCase() || "?"}
                          </div>
                          <span>{recruit.name || "—"}</span>
                        </div>
                      </td>
                      <td>{recruit.email || "—"}</td>
                      <td>{recruit.phone || "—"}</td>
                      <td>
                        <span className={`status-badge status-${recruit.status || "prospect"}`}>
                          {recruit.status || "prospect"}
                        </span>
                      </td>
                      <td>{recruit.level || "—"}</td>
                      <td>{recruit.leader || "—"}</td>
                      <td>
                        <span className="agency-code-badge">
                          {recruit.agencyCode || recruit.code || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Production Modal
function ProductionModal({ isOpen, onClose, row }) {
  const [production, setProduction] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen || !row) return;

    setLoading(true);
    const q = query(
      collection(db, DATA_SOURCES.productionCollection),
      where(FIELD_MAP.productionOwnerIdField, "==", row.id)
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const productionData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProduction(productionData);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading production:", error);
        setLoading(false);
      }
    );

    return unsub;
  }, [isOpen, row]);

  const filteredProduction = useMemo(() => {
    if (!searchTerm.trim()) return production;
    const term = searchTerm.toLowerCase();
    return production.filter(
      (entry) =>
        (entry.description || "").toLowerCase().includes(term) ||
        (entry.type || "").toLowerCase().includes(term)
    );
  }, [production, searchTerm]);

  const totalPoints = useMemo(() => {
    return filteredProduction.reduce(
      (sum, entry) => sum + (entry[FIELD_MAP.productionPointsField] || 0),
      0
    );
  }, [filteredProduction]);

  if (!isOpen) return null;

  return (
    <div className="tracker-modal-backdrop" onClick={onClose}>
      <div className="tracker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tracker-modal__head">
          <h3>
            {fmtInt(totalPoints)} Personal Points - {row.name}
          </h3>
          <button className="tracker-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="tracker-modal__body">
          <div className="modal-search-container">
            <input
              type="text"
              placeholder="Search production entries..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modal-search-input"
            />
          </div>
          <div className="modal-grid-container">
            {loading ? (
              <div className="modal-loading">Loading production...</div>
            ) : filteredProduction.length === 0 ? (
              <div className="modal-empty">
                {searchTerm
                  ? "No matching entries found"
                  : "No production entries found"}
              </div>
            ) : (
              <div className="compact-grid">
                <div className="compact-grid-header">
                  <div className="compact-grid-cell" style={{ width: "200px" }}>
                    Description
                  </div>
                  <div className="compact-grid-cell" style={{ width: "120px" }}>
                    Type
                  </div>
                  <div className="compact-grid-cell" style={{ width: "100px" }}>
                    Points
                  </div>
                  <div className="compact-grid-cell" style={{ width: "120px" }}>
                    Date
                  </div>
                </div>
                {filteredProduction.map((entry) => (
                  <div key={entry.id} className="compact-grid-row">
                    <div
                      className="compact-grid-cell"
                      style={{ width: "200px" }}
                    >
                      {entry.description || "—"}
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "120px" }}
                    >
                      {entry.type || "—"}
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "100px" }}
                    >
                      {fmtInt(entry[FIELD_MAP.productionPointsField] || 0)}
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "120px" }}
                    >
                      {entry.date
                        ? formatDateShort(entry.date.toDate?.() || entry.date)
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Licenses Modal
function LicensesModal({ isOpen, onClose, row }) {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!isOpen || !row) return;

    setLoading(true);

    const unsub = onSnapshot(
      collection(db, DATA_SOURCES.trackersCollection),
      (snapshot) => {
        const licenseData = [];
        const userRecruiterName = row.recruiter || row.name;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const licensingRecruiter = readPath(
            data,
            FIELD_MAP.licensingRecruiterField
          );

          // Match by ID or name
          if (
            licensingRecruiter === row.id ||
            licensingRecruiter === userRecruiterName
          ) {
            licenseData.push({
              id: doc.id,
              ...data,
            });
          }
        });

        setLicenses(licenseData);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading licenses:", error);
        setLoading(false);
      }
    );

    return unsub;
  }, [isOpen, row]);

  const filteredLicenses = useMemo(() => {
    if (!searchTerm.trim()) return licenses;
    const term = searchTerm.toLowerCase();
    return licenses.filter(
      (license) =>
        (license.name || "").toLowerCase().includes(term) ||
        (license.email || "").toLowerCase().includes(term)
    );
  }, [licenses, searchTerm]);

  if (!isOpen) return null;

  return (
    <div className="tracker-modal-backdrop" onClick={onClose}>
      <div className="tracker-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tracker-modal__head">
          <h3>
            {licenses.length} Licenses - {row.name}
          </h3>
          <button className="tracker-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="tracker-modal__body">
          <div className="modal-search-container">
            <input
              type="text"
              placeholder="Search licensed associates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="modal-search-input"
            />
          </div>
          <div className="modal-grid-container">
            {loading ? (
              <div className="modal-loading">Loading licenses...</div>
            ) : filteredLicenses.length === 0 ? (
              <div className="modal-empty">
                {searchTerm
                  ? "No matching licenses found"
                  : "No licenses found"}
              </div>
            ) : (
              <div className="compact-grid">
                <div className="compact-grid-header">
                  <div className="compact-grid-cell" style={{ width: "200px" }}>
                    Name
                  </div>
                  <div className="compact-grid-cell" style={{ width: "150px" }}>
                    Contact
                  </div>
                  <div className="compact-grid-cell" style={{ width: "120px" }}>
                    License Status
                  </div>
                  <div className="compact-grid-cell" style={{ width: "100px" }}>
                    Date
                  </div>
                </div>
                {filteredLicenses.map((license) => (
                  <div key={license.id} className="compact-grid-row">
                    <div
                      className="compact-grid-cell"
                      style={{ width: "200px" }}
                    >
                      {license.name || "—"}
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "150px" }}
                    >
                      <div>{license.email || "—"}</div>
                      <div>{license.phone || "—"}</div>
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "120px" }}
                    >
                      {license.licensing?.status || "Active"}
                    </div>
                    <div
                      className="compact-grid-cell"
                      style={{ width: "100px" }}
                    >
                      {license.licensing?.date
                        ? formatDateShort(
                          license.licensing.date.toDate?.() ||
                          license.licensing.date
                        )
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Columns & Groups
========================= */
const COLS = [
  { key: "name", label: "Name", width: 260, sortable: true, showLock: true },
  {
    key: "recruiter",
    label: "Recruiter",
    width: 200,
    sortable: true,
    showLock: true,
  },
  {
    key: "leader",
    label: "Leader",
    width: 200,
    sortable: true,
    showLock: true,
  },

  // PHILOSOPHY
  {
    key: "multiHanded",
    label: "Multi-\nhanded",
    type: "check",
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "tenThreeRulesGoals",
    label: "10% -\n3 RULES -\n3 GOALS",
    type: "check",
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "selfImprovement",
    label: "Self-\nImprovement",
    type: "check",
    width: 140,
    center: true,
    sortable: true,
  },

  // SYSTEM
  {
    key: "obsRecruitsDone",
    label: "Observe 4\nrecruits",
    type: "check",
    width: 140,
    readOnly: true,
    center: true,
    sortable: true,
  },
  {
    key: "obsClientsDone",
    label: "Observe 4\nclients",
    type: "check",
    readOnly: true,
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "getLicense",
    label: "Get licensed",
    type: "check",
    readOnly: true, // Auto-synced from Licensing Tracker
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "registerConvention",
    label: "1 Direct\nRegistration",
    type: "check",
    width: 140,
    center: true,
    sortable: true,
  },

  // BUILD
  {
    key: "recruitsTtl",
    label: "9 Recruits",
    type: "statChip",
    width: 120,
    center: true,
    sortable: true,
  },
  {
    key: "personalPoints",
    label: "45k Personal\nPoints",
    type: "pointsChip",
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "licensesTtl",
    label: "3 Licenses",
    type: "statChip",
    width: 120,
    center: true,
    sortable: true,
  },
  {
    key: "registrationsBase",
    label: "15 Registration\nbase",
    type: "number",
    width: 140,
    center: true,
    sortable: true,
  },
  {
    key: "netLicense",
    label: "Net Licensed",
    type: "netLicensePopup",
    width: 120,
    center: true,
  },
  {
    key: "keyPlayer",
    label: "Key Player",
    type: "check",
    width: 120,
    center: true,
    sortable: true,
  },
  {
    key: "training",
    label: "Training",
    type: "check",
    width: 120,
    center: true,
    sortable: true,
  },
  {
    key: "bigEvent",
    label: "Big Event",
    type: "check",
    width: 120,
    center: true,
    sortable: true,
  },
  { key: "why", label: "Why", type: "notesInline", width: 320, center: false },
  { key: "goal", label: "Goal", type: "notesInline", width: 320, center: false },
];

const GROUPS = [
  {
    key: "philosophy",
    label: "PHILOSOPHY",
    icon: "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/missionpin.png?alt=media&token=c6534897-de60-4981-912c-4b73802cc918",
    from: "multiHanded",
    to: "selfImprovement",
  },
  {
    key: "system",
    label: "SYSTEM",
    icon: "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/4x4logo.png?alt=media&token=4867ff8e-efdd-4933-805d-01f193837337",
    from: "obsRecruitsDone",
    to: "registerConvention",
  },
  {
    key: "build",
    label: "BUILD",
    icon: "https://firebasestorage.googleapis.com/v0/b/wealthbuilders-crm-9c323.firebasestorage.app/o/ChatGPT%20Image%20Sep%2014%2C%202025%2C%2003_53_38%20PM.png?alt=media&token=38dff29f-b9a7-493a-939d-9c868cb495d2",
    from: "recruitsTtl",
    to: "goal",
  },
];

/* =========================
   Net License Amount Modal Component
========================= */
function NetLicenseModal({ isOpen, onClose, row, onSave }) {
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    if (isOpen && row) {
      setAmount(row.netLicenseAmount || 0);
    }
  }, [isOpen, row]);

  const handleSave = () => {
    if (row) {
      onSave(row.id, amount);
    }
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  if (!isOpen || !row) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Net Licensed Amount</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="input-group">
            <label>Enter amount for {row.name || "this user"}:</label>
            <div className="amount-input-container">
              <span className="currency-symbol">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                onKeyPress={handleKeyPress}
                className="amount-input"
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" onClick={handleSave}>
            Save Amount
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Grid Component
========================= */
function ResizableGrid({
  columns,
  groups,
  rows,
  loading,
  emptyText,
  renderCell,
  rowKey,
  lockableLeftCount = 3,
  initialLockedKeys = ["name", "recruiter", "leader"],
  renderHeaderSearch,
  sortKey,
  sortDir,
  onSort,
  rowStyle,
  onScroll,
  loadingMore,
}) {
  const LOCKABLE_SET = useMemo(
    () => new Set(columns.slice(0, lockableLeftCount).map((c) => c.key)),
    [columns, lockableLeftCount]
  );

  const storageKey = "tracker-associate-columns";

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedWidths = JSON.parse(saved);
        const initialWidths = Object.fromEntries(
          columns.map((c) => [c.key, c.width || 140])
        );
        return { ...initialWidths, ...savedWidths };
      }
    } catch (e) {
      console.warn("Failed to load associate column widths:", e);
    }
    return Object.fromEntries(columns.map((c) => [c.key, c.width || 140]));
  });

  const [lockedKeys, setLockedKeys] = useState(
    () => new Set(initialLockedKeys)
  );

  const contiguousLockedKeys = useMemo(() => {
    const out = [];
    for (let i = 0; i < lockableLeftCount && i < columns.length; i++) {
      const key = columns[i].key;
      if (lockedKeys.has(key)) out.push(key);
      else break;
    }
    return out;
  }, [columns, lockableLeftCount, lockedKeys]);

  const lockedColumns = useMemo(
    () => columns.filter((c) => contiguousLockedKeys.includes(c.key)),
    [columns, contiguousLockedKeys]
  );

  const scrollableColumns = useMemo(
    () => columns.filter((c) => !contiguousLockedKeys.includes(c.key)),
    [columns, contiguousLockedKeys]
  );

  const lockedBlockWidth = useMemo(
    () =>
      lockedColumns.reduce(
        (sum, col) => sum + (colWidths[col.key] ?? (col.width || 140)),
        0
      ),
    [lockedColumns, colWidths]
  );

  const totalScrollWidth = useMemo(
    () =>
      scrollableColumns.reduce(
        (sum, col) => sum + (colWidths[col.key] ?? (col.width || 140)),
        0
      ),
    [scrollableColumns, colWidths]
  );

  const scrollColumnOffsets = useMemo(() => {
    let acc = 0;
    const offsets = {};
    scrollableColumns.forEach((col) => {
      offsets[col.key] = acc;
      acc += colWidths[col.key] ?? (col.width || 140);
    });
    return offsets;
  }, [scrollableColumns, colWidths]);

  const groupSpans = useMemo(() => {
    return groups
      .map((group) => {
        const startIdx = scrollableColumns.findIndex(
          (c) => c.key === group.from
        );
        const endIdx = scrollableColumns.findIndex((c) => c.key === group.to);
        if (startIdx === -1 || endIdx === -1 || endIdx < startIdx) {
          return null;
        }

        const keys = scrollableColumns
          .slice(startIdx, endIdx + 1)
          .map((c) => c.key);
        const width = keys.reduce(
          (sum, key) =>
            sum +
            (colWidths[key] ??
              (columns.find((c) => c.key === key)?.width || 140)),
          0
        );
        const left = scrollColumnOffsets[keys[0]] ?? 0;

        return { ...group, width, left };
      })
      .filter(Boolean);
  }, [groups, scrollableColumns, colWidths, columns, scrollColumnOffsets]);

  const saveColumnWidths = useCallback(
    (newWidths) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(newWidths));
      } catch (e) {
        console.warn("Failed to save associate column widths:", e);
      }
    },
    [storageKey]
  );

  const dragRef = useRef(null);
  const lockedRowsRef = useRef(null);
  const rowsRef = useRef(null);
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(110, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      if (dragRef.current) {
        saveColumnWidths(colWidths);
      }
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [colWidths, saveColumnWidths]);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const toggleLock = (key) => {
    if (!LOCKABLE_SET.has(key)) return;
    setLockedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resolveRowStyle = (row) => {
    if (typeof rowStyle === "function") return rowStyle(row) || {};
    if (rowStyle && typeof rowStyle === "object") return rowStyle;
    return {};
  };

  const BAND_HEIGHT = 60;
  const SUBHEAD_HEIGHT = 64;
  const SUBHEAD_OFFSET = `calc(var(--filters-height, 0px) + ${BAND_HEIGHT}px)`;
  const LOCKED_HEADER_HEIGHT = BAND_HEIGHT + SUBHEAD_HEIGHT;

  const renderLockedHeader = () => {
    if (!lockedColumns.length) return null;
    return (
      <div
        className="rg-row rg-header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 24,
          width: lockedBlockWidth,
          height: `${LOCKED_HEADER_HEIGHT}px`,
          display: "flex",
          background: "#252830",
        }}
      >
        {lockedColumns.map((c, index) => {
          const w = colWidths[c.key] ?? (c.width || 140);
          const isSorted = c.sortable && c.key === sortKey;
          const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";

          return (
            <div
              key={c.key}
              className={`rg-cell rg-head locked ${c.center ? "center" : ""}`}
              style={{
                width: w,
                minWidth: w,
                maxWidth: w,
                height: `${LOCKED_HEADER_HEIGHT}px`,
                minHeight: `${LOCKED_HEADER_HEIGHT}px`,
                maxHeight: `${LOCKED_HEADER_HEIGHT}px`,
                alignItems: "stretch",
                // Don't override padding - use CSS default to match scrollable headers
              }}
            >
              <div
                className="rg-head-inner"
                style={{ height: "100%", justifyContent: "space-between" }}
              >
                <div
                  className="rg-head-top"
                  style={{ alignItems: "flex-start" }}
                >
                  <div className="rg-title-group">
                    {index < lockableLeftCount && (
                      <button
                        type="button"
                        className="lock-btn on"
                        onClick={() => toggleLock(c.key)}
                        title="Unlock column"
                      >
                        🔒
                      </button>
                    )}
                    <span className="rg-title">{c.label}</span>
                  </div>
                  <div className="rg-actions-group">
                    {c.sortable && (
                      <button
                        type="button"
                        className={`mini-btn sort-btn ${isSorted ? "on" : ""}`}
                        onClick={() => onSort && onSort(c.key)}
                        title={isSorted ? `Sorted ${sortDir}` : "Sort"}
                      >
                        {sortLabel}
                      </button>
                    )}
                  </div>
                </div>
                <div className="rg-head-search" style={{ marginTop: "auto" }}>
                  {renderHeaderSearch ? renderHeaderSearch(c) : null}
                </div>
              </div>
              <span
                className="rg-grip"
                onMouseDown={(e) => startDrag(c.key, e)}
                title="Drag to resize"
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderLockedRows = () => {
    if (loading || !rows.length) return null;
    return (
      <div className="rg-locked-rows" ref={lockedRowsRef}>
        {rows.map((row, rowIdx) => {
          const styleVars = resolveRowStyle(row);
          return (
            <div
              className="rg-row"
              key={rowKey ? rowKey(row) : rowIdx}
              style={styleVars}
            >
              {lockedColumns.map((c) => {
                const w = colWidths[c.key] ?? (c.width || 140);
                return (
                  <div
                    key={c.key}
                    className={`rg-cell locked ${c.center ? "center" : ""}`}
                    style={{ width: w, minWidth: w, maxWidth: w }}
                  >
                    {renderCell(row, c, rowIdx)}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderScrollableBand = () => {
    if (!scrollableColumns.length) return null;
    return (
      <div
        className="rg-row rg-header rg-groupband"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 18,
          height: `${BAND_HEIGHT}px`,
          minWidth: `${totalScrollWidth}px`,
          background: "#252830",
        }}
      >
        <div className="rg-groupband-inner" style={{ width: totalScrollWidth }}>
          {groupSpans.map((group) => (
            <div
              key={group.key}
              className="rg-group"
              style={{ width: group.width, left: group.left }}
            >
              <div className="rg-group-title">
                {group.icon && (
                  <img src={group.icon} alt="" className="rg-group-icon" />
                )}
                <span>{group.label}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderScrollableHeader = () => {
    if (!scrollableColumns.length) return null;
    return (
      <div
        className="rg-row rg-header rg-subhead"
        style={{
          position: "sticky",
          top: `${BAND_HEIGHT}px`,
          zIndex: 12,
          minWidth: `${totalScrollWidth}px`,
          background: "#252830",
        }}
      >
        {scrollableColumns.map((c) => {
          const w = colWidths[c.key] ?? (c.width || 140);
          const isSorted = c.sortable && c.key === sortKey;
          const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";
          const originalIndex = columns.findIndex((col) => col.key === c.key);
          const isGroupEdge = [
            "selfImprovement",
            "registerConvention",
            "goal",
          ].includes(c.key);

          return (
            <div
              key={c.key}
              className={`rg-cell rg-head ${isGroupEdge ? "group-edge" : ""} ${c.center ? "center" : ""
                }`}
              style={{ width: w, minWidth: w, maxWidth: w }}
            >
              <div className="rg-head-inner">
                <div className="rg-head-top">
                  <div className="rg-title-group">
                    {originalIndex < lockableLeftCount && (
                      <button
                        type="button"
                        className={`lock-btn ${lockedKeys.has(c.key) ? "on" : ""
                          }`}
                        onClick={() => toggleLock(c.key)}
                        title={
                          lockedKeys.has(c.key)
                            ? "Unlock column"
                            : "Lock column"
                        }
                      >
                        {lockedKeys.has(c.key) ? "🔒" : "🔓"}
                      </button>
                    )}
                    <span className="rg-title">{c.label}</span>
                  </div>
                  <div className="rg-actions-group">
                    {c.sortable && (
                      <button
                        type="button"
                        className={`mini-btn sort-btn ${isSorted ? "on" : ""}`}
                        onClick={() => onSort && onSort(c.key)}
                        title={isSorted ? `Sorted ${sortDir}` : "Sort"}
                      >
                        {sortLabel}
                      </button>
                    )}
                  </div>
                </div>
                <div className="rg-head-search">
                  {renderHeaderSearch ? renderHeaderSearch(c) : null}
                </div>
              </div>
              <span
                className="rg-grip"
                onMouseDown={(e) => startDrag(c.key, e)}
                title="Drag to resize"
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderScrollableRows = () => {
    if (loading) {
      return <div className="rg-empty">Loading…</div>;
    }
    if (!rows.length) {
      return <div className="rg-empty">{emptyText || "No data"}</div>;
    }

    return (
      <>
        {rows.map((row, rowIdx) => {
      const styleVars = resolveRowStyle(row);
      return (
        <div
          className="rg-row"
          key={rowKey ? rowKey(row) : rowIdx}
          style={{ ...styleVars, minWidth: `${totalScrollWidth}px` }}
        >
          {scrollableColumns.map((c) => {
            const w = colWidths[c.key] ?? (c.width || 140);
            const isGroupEdge = [
              "selfImprovement",
              "registerConvention",
              "goal",
            ].includes(c.key);
            return (
              <div
                key={c.key}
                className={`rg-cell ${c.center ? "center" : ""} ${isGroupEdge ? "group-edge" : ""
                  }`}
                style={{ width: w, minWidth: w, maxWidth: w }}
              >
                {renderCell(row, c, rowIdx)}
              </div>
            );
          })}
        </div>
      );
        })}
        {loadingMore && rows.length > 0 && (
          <div className="rg-loading-more" style={{ padding: '20px', textAlign: 'center', color: '#999', fontStyle: 'italic' }}>
            Loading more users...
          </div>
        )}
      </>
    );
  };

  // Refs for scroll synchronization
  const lockedSectionRef = useRef(null);
  const scrollSectionRef = useRef(null);

  // Sync scrolling between locked and scrollable sections
  useEffect(() => {
    const lockedEl = lockedSectionRef.current;
    const scrollEl = scrollSectionRef.current;
    if (!lockedEl || !scrollEl) return;

    let isSyncing = false; // Flag to prevent infinite loop

    const handleLockedScroll = () => {
      if (!isSyncing && lockedEl && scrollEl) {
        isSyncing = true;
        // Direct sync - both sections have the same header height (LOCKED_HEADER_HEIGHT = BAND_HEIGHT + SUBHEAD_HEIGHT)
        scrollEl.scrollTop = lockedEl.scrollTop;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    const handleScrollScroll = () => {
      if (!isSyncing && lockedEl && scrollEl) {
        isSyncing = true;
        // Direct sync - both sections have the same header height
        lockedEl.scrollTop = scrollEl.scrollTop;
        requestAnimationFrame(() => {
          isSyncing = false;
        });
      }
    };

    lockedEl.addEventListener('scroll', handleLockedScroll, { passive: true });
    scrollEl.addEventListener('scroll', handleScrollScroll, { passive: true });

    return () => {
      lockedEl.removeEventListener('scroll', handleLockedScroll);
      scrollEl.removeEventListener('scroll', handleScrollScroll);
    };
  }, [lockedColumns.length]); // Re-run when locked columns change

  return (
    <div className="rg-wrap">
      <div className="rg-layout">
        <div
          ref={lockedSectionRef}
          className="rg-locked-section"
          style={{ width: lockedBlockWidth, minWidth: lockedBlockWidth }}
        >
          {renderLockedHeader()}
          {renderLockedRows()}
        </div>

        <div className="rg-scroll-section">
          <div 
            ref={scrollSectionRef} 
            className="rg-scroll"
            onScroll={(e) => {
              if (!onScroll) return;
              const { scrollTop, scrollHeight, clientHeight } = e.target;
              // Trigger load more when scrolled to bottom (with 200px threshold)
              if (scrollHeight - scrollTop - clientHeight < 200) {
                onScroll();
              }
            }}
          >
            {renderScrollableBand()}
            {renderScrollableHeader()}
            <div className="rg-rows" ref={rowsRef}>
              {renderScrollableRows()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Prospects Modal Component - Full-Featured with Resizable Columns & Notes
========================= */
function ProspectsModal({ isOpen, onClose, filterType, filterValue, userName, userId, modalSource }) {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });

  // Column widths state (resizable)
  const [colWidths, setColWidths] = useState({
    mark: 42,
    leader: 180,
    recruiter: 180,
    name: 240,
    top25: 90,
    hot: 80,
    outcome: 150,
    profile: 520,
    notes: 520,
    actions: 180
  });

  // Notes modal state
  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);

  // Resizing state
  const dragRef = useRef(null);

  // Column resizing handlers
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(80, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  // Notes functions (same as ProspectTracker)
  const prospectsRef = (id) => doc(db, "prospects", id);

  const addNoteInternal = async (rid, baseNotes, text) => {
    const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";
    
    // Add note using centralized notes service
    const noteId = await addNote({
      prospectId: rid,
      text: text.trim(),
      createdBy: recruiterName,
      tracker: 'prospect'
    });

    // Update local state with new note
    const now = new Date();
    const newNote = {
      id: noteId,
      text: text.trim(),
      createdAt: now.getTime(),
      createdAtISO: now.toISOString(),
      createdBy: recruiterName,
    };

    setProspects(prev => prev.map(p => {
      if (p.id === rid) {
        const next = Array.isArray(p.notes) ? [...p.notes, newNote] : [newNote];
        return { ...p, notes: next };
      }
      return p;
    }));
  };

  const addInlineNote = async (prospect) => {
    const text = (noteDrafts[prospect.id] || "").trim();
    if (!text) return;
    try {
      setNoteSaving(true);
      const base = Array.isArray(prospect.notes) ? prospect.notes : [];
      await addNoteInternal(prospect.id, base, text);
      setNoteDrafts((d) => ({ ...d, [prospect.id]: "" }));
    } catch (e) {
      console.error(e);
    } finally {
      setNoteSaving(false);
    }
  };

  const deleteNote = async (prospect, noteId) => {
    if (!prospect) return;
    setNoteDeletingId(noteId);
    try {
      // Delete note using centralized notes service
      await deleteNoteFromService(noteId);

      // Update local state
      setProspects(prev => prev.map(p => {
        if (p.id === prospect.id) {
          return { ...p, notes: (p.notes || []).filter((n) => n.id !== noteId) };
        }
        return p;
      }));
    } catch (e) {
      console.error(e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  // ✅ Load prospects with client-side filtering (to avoid composite index requirement)
  useEffect(() => {
    if (!isOpen) {
      setProspects([]);
      return;
    }

    setLoading(true);

    // Load ALL active prospects (no compound where clauses to avoid index requirement)
    const q = query(
      collection(db, "prospects"),
      where("status", "!=", "inactive")
    );

    const unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: false }, // ✅ Ignore metadata-only changes
      (snapshot) => {
        let data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Client-side filtering based on popup type
        if ((filterType === 'allProspects' || filterType === 'recruitsOnly' || filterType === 'clientsOnly') && userId) {
          data = data.filter(p => {
            // Match by recruiterId OR by recruiter name (for legacy prospects)
            const matchesRecruiterById = p.recruiterId === userId;
            const matchesRecruiterByName = p.recruiter === userName;
            const matchesRecruiter = matchesRecruiterById || (matchesRecruiterByName && (!p.recruiterId || p.recruiterId === 'undefined'));

            // If 'allProspects' (9 Recruits), show all prospects by this person
            if (filterType === 'allProspects') {
              return matchesRecruiter;
            }

            // If 'clientsOnly' (45k Personal Points), filter for clients
            if (filterType === 'clientsOnly') {
              const outcome = (p.outcome || "").toLowerCase();
              // Treat undefined/empty as "client" (default state)
              const matchesOutcome = !outcome || outcome === 'client' || outcome === 'both';

              return matchesRecruiter && matchesOutcome;
            }

            // If 'recruitsOnly' (3 Licenses), filter for recruits
            if (filterType === 'recruitsOnly') {
              const outcome = (p.outcome || "").toLowerCase();
              const matchesOutcome = outcome === 'recruit' || outcome === 'both';
              return matchesRecruiter && matchesOutcome;
            }

            return false;
          });
        }

        setProspects(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error loading prospects:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isOpen, filterType, userId]);

  // Subscribe to notes for prospects
  useEffect(() => {
    if (!isOpen || prospects.length === 0) return;

    const unsubscribers = [];

    prospects.forEach(prospect => {
      // Subscribe to notes for this prospect using the notes service
      const unsubscribe = subscribeToProspectNotes(prospect.id, (notes) => {
        // Update prospect with fetched notes
        setProspects(prev => prev.map(p => 
          p.id === prospect.id ? { ...p, notes } : p
        ));
      });

      if (unsubscribe) {
        unsubscribers.push(unsubscribe);
      }
    });

    return () => {
      unsubscribers.forEach(unsub => unsub && unsub());
    };
  }, [isOpen, prospects.length]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '⇅';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  // ✅ Filter and sort in memory (already limited to 50)
  const filteredProspects = useMemo(() => {
    let filtered = prospects;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        (p.name?.toLowerCase() || "").includes(term) ||
        (p.leader?.toLowerCase() || "").includes(term) ||
        (p.recruiter?.toLowerCase() || "").includes(term) ||
        (p.email?.toLowerCase() || "").includes(term) ||
        (p.phone?.toLowerCase() || "").includes(term)
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        if (sortConfig.direction === 'asc') {
          return aVal > bVal ? 1 : -1;
        }
        return aVal < bVal ? 1 : -1;
      });
    }

    return filtered;
  }, [prospects, searchTerm, sortConfig]);

  // Current notes for the open row (MUST be before early return)
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    const live = prospects.find((r) => r.id === notesOpenFor.id) || notesOpenFor;
    return Array.isArray(live.notes) ? live.notes : [];
  }, [notesOpenFor, prospects]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  // Early return AFTER all hooks
  if (!isOpen) return null;

  // Dynamic title based on filter type and modal source
  let title = `All Prospects (${filteredProspects.length})`;
  if (filterType === 'allProspects' && userId && userName) {
    title = `${userName}'s Prospects (${filteredProspects.length})`;
  } else if (filterType === 'clientsOnly' && userId && userName) {
    title = `${userName}'s Personal Points - Clients (${filteredProspects.length})`;
  } else if (filterType === 'recruitsOnly' && userId && userName) {
    title = `${userName}'s Licenses - Recruits (${filteredProspects.length})`;
  }

  // Get row colors
  const getRowColors = (prospect) => {
    const mark = (prospect.mark || "default").toLowerCase();
    switch (mark) {
      case "client":
        return {
          bg: "rgba(0, 179, 92, 0.15)",
          bgSolid: "#004d2e",  // Fully opaque dark green - NO TRANSPARENCY
          border: "rgba(0, 230, 118, 0.3)"
        };
      case "recruit":
        return {
          bg: "rgba(35, 102, 217, 0.15)",
          bgSolid: "#0d2956",  // Fully opaque dark blue - NO TRANSPARENCY
          border: "rgba(41, 121, 255, 0.3)"
        };
      case "both":
        return {
          bg: "rgba(204, 143, 0, 0.15)",
          bgSolid: "#5c3d00",  // Fully opaque dark gold - NO TRANSPARENCY
          border: "rgba(255, 179, 0, 0.3)"
        };
      default:
        return {
          bg: "#1a1a1f",  // Changed to solid for consistency
          bgSolid: "#1a1a1f",  // Fully opaque dark gray
          border: "rgba(42, 46, 55, 1)"
        };
    }
  };

  return (
    <>
      <div className="modal-overlay-glass" onClick={onClose}>
        <div className="prospects-modal-glass-v2" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="prospects-modal-header-glass">
            <h2>{title}</h2>
            <button className="prospects-modal-close-glass" onClick={onClose}>✕</button>
          </div>

          {/* Search */}
          <div className="prospects-modal-search">
            <input
              type="text"
              placeholder="Search prospects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="prospects-search-input-glass"
            />
          </div>

          {/* Resizable Table */}
          <div className="prospects-table-wrapper-resizable">
            {loading ? (
              <div className="prospects-loading-glass">Loading...</div>
            ) : filteredProspects.length === 0 ? (
              <div className="prospects-empty-glass-v2">
                {searchTerm ? "No matching prospects found" : "No prospects yet"}
              </div>
            ) : (
              <div className="prospects-table-scroll">
                <table className="prospects-table-resizable">
                  <thead>
                    <tr>
                      {/* Marker */}
                      <th
                        style={{
                          width: colWidths.mark,
                          minWidth: colWidths.mark,
                          position: 'relative'
                        }}
                      >
                        <span className="resize-handle" onMouseDown={(e) => startDrag("mark", e)} />
                      </th>
                      {/* Leader */}
                      <th
                        onClick={() => handleSort("leader")}
                        style={{
                          width: colWidths.leader,
                          minWidth: colWidths.leader,
                          position: 'relative'
                        }}
                      >
                        Leader {getSortIcon("leader")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("leader", e)} />
                      </th>
                      {/* Recruiter */}
                      <th
                        onClick={() => handleSort("recruiter")}
                        style={{
                          width: colWidths.recruiter,
                          minWidth: colWidths.recruiter,
                          position: 'relative'
                        }}
                      >
                        Recruiter {getSortIcon("recruiter")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("recruiter", e)} />
                      </th>
                      {/* Name */}
                      <th
                        onClick={() => handleSort("name")}
                        style={{
                          width: colWidths.name,
                          minWidth: colWidths.name,
                          position: 'relative'
                        }}
                      >
                        Name {getSortIcon("name")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("name", e)} />
                      </th>
                      {/* Top 25 */}
                      <th style={{ width: colWidths.top25, minWidth: colWidths.top25, textAlign: 'center', position: 'relative' }}>
                        Top 25
                        <span className="resize-handle" onMouseDown={(e) => startDrag("top25", e)} />
                      </th>
                      {/* HOT */}
                      <th style={{ width: colWidths.hot, minWidth: colWidths.hot, textAlign: 'center', position: 'relative' }}>
                        HOT
                        <span className="resize-handle" onMouseDown={(e) => startDrag("hot", e)} />
                      </th>
                      {/* Outcome */}
                      <th onClick={() => handleSort("outcome")} style={{ width: colWidths.outcome, minWidth: colWidths.outcome, position: 'relative' }}>
                        Outcome {getSortIcon("outcome")}
                        <span className="resize-handle" onMouseDown={(e) => startDrag("outcome", e)} />
                      </th>
                      {/* Profile */}
                      <th style={{ width: colWidths.profile, minWidth: colWidths.profile, position: 'relative' }}>
                        Profile
                        <span className="resize-handle" onMouseDown={(e) => startDrag("profile", e)} />
                      </th>
                      {/* Notes */}
                      <th style={{ width: colWidths.notes, minWidth: colWidths.notes, position: 'relative' }}>
                        Notes
                        <span className="resize-handle" onMouseDown={(e) => startDrag("notes", e)} />
                      </th>
                      {/* Actions */}
                      <th style={{ width: colWidths.actions, minWidth: colWidths.actions, position: 'relative' }}>
                        Actions
                        <span className="resize-handle" onMouseDown={(e) => startDrag("actions", e)} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProspects.map((prospect) => {
                      const colors = getRowColors(prospect);
                      const isNoteFocused = focusedNoteInputId === prospect.id;
                      const notesArr = Array.isArray(prospect.notes) ? prospect.notes : [];
                      const lastNote = notesArr.length > 0
                        ? notesArr.slice().reverse().find(n => n.text)
                        : null;
                      const inputValue = isNoteFocused
                        ? (noteDrafts[prospect.id] || "")
                        : (noteDrafts[prospect.id] || (lastNote?.text || ""));

                      return (
                        <tr
                          key={prospect.id}
                          style={{
                            backgroundColor: colors.bg,
                            borderLeft: `3px solid ${colors.border}`
                          }}
                        >
                          {/* Marker */}
                          <td style={{ width: colWidths.mark, textAlign: 'center' }}>
                            <div
                              className="prospect-marker-inline"
                              style={{
                                backgroundColor: prospect.mark === 'client' ? '#00E676' :
                                  prospect.mark === 'recruit' ? '#2979FF' :
                                    prospect.mark === 'both' ? '#FFB300' : '#cfcfcf'
                              }}
                            />
                          </td>
                          {/* Leader */}
                          <td style={{ width: colWidths.leader }}>
                            {prospect.leader || "—"}
                          </td>
                          {/* Recruiter */}
                          <td style={{ width: colWidths.recruiter }}>
                            {prospect.recruiter || "—"}
                          </td>
                          {/* Name */}
                          <td style={{ width: colWidths.name }}>
                            {prospect.name || "—"}
                          </td>
                          {/* Top 25 */}
                          <td style={{ width: colWidths.top25, textAlign: 'center', color: prospect.top25 ? '#00E676' : '#FF3D00' }}>
                            {prospect.top25 ? "✓" : "X"}
                          </td>
                          {/* HOT */}
                          <td style={{ width: colWidths.hot, textAlign: 'center', color: prospect.hot ? '#00E676' : '#FF3D00' }}>
                            {prospect.hot ? "✓" : "X"}
                          </td>
                          {/* Outcome */}
                          <td style={{ width: colWidths.outcome }}>
                            {prospect.outcome ? (
                              <span className={`outcome-badge-inline ${prospect.outcome?.toLowerCase() || ""}`}>
                                {prospect.outcome}
                              </span>
                            ) : "—"}
                          </td>
                          {/* Profile */}
                          <td style={{ width: colWidths.profile, fontSize: '12px' }}>
                            {(() => {
                              const p = prospect.profile || {};
                              const age = p.birthday ? (() => {
                                const birthDate = new Date(p.birthday);
                                const today = new Date();
                                let age = today.getFullYear() - birthDate.getFullYear();
                                const m = today.getMonth() - birthDate.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
                                return age >= 0 ? age : null;
                              })() : null;

                              const parts = [
                                p.howKnown,
                                p.relationship || p.relationship === 0 ? `${p.relationship}/10` : null,
                                p.occupation,
                                p.relationshipStatus,
                                p.dependentChildren,
                                age !== null ? `Age: ${age}` : null,
                              ].filter(x => x !== null && x !== undefined && x !== "");

                              return parts.length > 0 ? parts.join(" • ") : "—";
                            })()}
                          </td>
                          {/* Notes with inline editing */}
                          <td style={{ width: colWidths.notes }}>
                            <div className="notes-cell-inline">
                              <input
                                className={`note-input-inline ${!isNoteFocused && lastNote && !noteDrafts[prospect.id] ? "showing-existing" : ""}`}
                                type="text"
                                placeholder="Add a note..."
                                value={inputValue}
                                onChange={(e) =>
                                  setNoteDrafts((d) => ({ ...d, [prospect.id]: e.target.value }))
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && (noteDrafts[prospect.id] || "").trim()) {
                                    addInlineNote(prospect);
                                  }
                                }}
                                onFocus={() => {
                                  setFocusedNoteInputId(prospect.id);
                                  if (!noteDrafts[prospect.id]) {
                                    setNoteDrafts((d) => ({ ...d, [prospect.id]: "" }));
                                  }
                                }}
                                onBlur={() => setFocusedNoteInputId(null)}
                              />
                              <button
                                className="notes-openall-btn"
                                onClick={() => setNotesOpenFor(prospect)}
                                title="View all notes"
                              >
                                ✏️
                              </button>
                            </div>
                            {!isNoteFocused && lastNote && !noteDrafts[prospect.id] && (
                              <div className="note-meta-inline">
                                {lastNote.createdBy || "—"} • {
                                  lastNote.createdAtISO
                                    ? formatDateTimePretty(lastNote.createdAtISO)
                                    : tsToMs(lastNote.createdAt)
                                      ? formatDateTimePretty(
                                        new Date(tsToMs(lastNote.createdAt)).toISOString()
                                      )
                                      : ""
                                }
                              </div>
                            )}
                          </td>
                          {/* Actions */}
                          <td style={{ width: colWidths.actions }}>
                            <div className="actions-inline-btns">
                              {prospect.phone && (
                                <a href={`tel:${prospect.phone}`} className="action-btn-inline" title="Call">
                                  📞
                                </a>
                              )}
                              {prospect.email && (
                                <a href={`mailto:${prospect.email}`} className="action-btn-inline" title="Email">
                                  ✉️
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer with count */}
          {!loading && filteredProspects.length > 0 && (
            <div className="prospects-modal-footer-glass">
              Showing {filteredProspects.length} prospect{filteredProspects.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {/* Notes Modal */}
      {notesOpenFor && (
        <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head">
              <h3>Notes — {notesOpenFor.name}</h3>
              <button
                className="btn ghost"
                onClick={() => setNotesOpenFor(null)}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal">
                  <div className="note-text-modal">{n.text}</div>
                  <div className="note-meta-modal">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : tsToMs(n.createdAt)
                        ? formatDateTimeISO(
                          new Date(tsToMs(n.createdAt)).toISOString()
                        )
                        : ""}
                  </div>
                  <button
                    className="note-delete-modal"
                    onClick={() => deleteNote(notesOpenFor, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add">
              <textarea
                className="input"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn primary"
                onClick={() => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (text) {
                    addNoteInternal(
                      notesOpenFor.id,
                      notesOpenFor.notes,
                      text
                    );
                    setNoteDrafts((d) => ({ ...d, modal: "" }));
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* =========================
   Helper functions for notes
========================= */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}

function tsToMs(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

/* =========================
   Page Component
========================= */
export default function AssociateTracker() {
  const { isFeatureEnabled } = useUIVersion();
  const { user: currentUser } = useAuth(); // useAuth returns 'user', alias as 'currentUser' for existing code
  const { showToast } = useToast();
  const { plan: accountPlan } = useAccountType();
  const modernTrackersEnabled = isFeatureEnabled("modernTrackers");
  const modernNotesEnabled = isFeatureEnabled("modernTrackerNotes");
  const modernFiltersEnabled = isFeatureEnabled("modernTrackerFilters");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");

  // Hierarchy filtering
  const [allUsers, setAllUsers] = useState([]);
  const [allowedUserIds, setAllowedUserIds] = useState(new Set());

  const [nameQ, setNameQ] = useState("");
  const [recruiterQ, setRecruiterQ] = useState("");
  const [leaderQ, setLeaderQ] = useState("");

  // ✅ Debounce search filters to avoid immediate query firing
  const debouncedNameQ = useDebounce(nameQ, 500);
  const debouncedRecruiterQ = useDebounce(recruiterQ, 500);
  const debouncedLeaderQ = useDebounce(leaderQ, 500);

  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");

  // Modern filtering state
  const [dateFilter, setDateFilter] = useState("all");
  const [baseShopFilter, setBaseShopFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({
    start: "",
    end: "",
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [netLicenseModal, setNetLicenseModal] = useState({
    isOpen: false,
    row: null,
  });

  // Notes state for Why and Goal
  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [notesType, setNotesType] = useState(null); // 'why' or 'goal'
  const [noteDrafts, setNoteDrafts] = useState({});
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);
  const [noteDeletingId, setNoteDeletingId] = useState(null);

  // Modal states for drill-down trackers - REMOVED (now using navigation)

  // cursor for Firestore paging
  const pageStateRef = useRef({
    nextCursor: null,
    pageSize: 50,
    baseQueryKey: "default",
  });

  // live listeners for trackers docs
  const trackerUnsubsRef = useRef(new Map()); // userId -> unsub

  // Track initialized users to prevent multiple ensureAssociateExists calls
  const initializedUsersRef = useRef(new Set());

  const {
    id: profileId,
    openWithId: openProfile,
    close: closeProfile,
  } = useProfileModalRoute();
  const [profileData, setProfileData] = useState(null);
  const [prospectsModal, setProspectsModal] = useState({ open: false, filterType: null, filterValue: '', userName: '', userId: '', modalSource: '' });

  // ❌ DISABLED: Data watchers were causing excessive Firebase queries (150+ listeners for 50 users)
  // ✅ Instead, we read from associate.* fields which are already in the tracker document
  // const { startWatchersForRow, cleanupWatchers } = useDataWatchers();

  // Fetch all users to build hierarchy
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(users);
    });
    return () => unsub();
  }, []);

  // Compute which users can be viewed based on account type and hierarchy
  useEffect(() => {
    if (!currentUser) {
      setAllowedUserIds(new Set());
      return;
    }
    const allowed = buildAccessibleUsers(
      accountPlan,
      allUsers,
      currentUser.uid
    );
    setAllowedUserIds(allowed);
  }, [currentUser, accountPlan, allUsers]);

  // Update row data helper
  const updateRowData = useCallback((userId, patch) => {
    setRows((prev) =>
      prev.map((row) => (row.id === userId ? { ...row, ...patch } : row))
    );
  }, []);

  // Licensing listener ref
  const licensingUnsubsRef = useRef(new Map());

  // Initial page load
  useEffect(() => {
    let cancelled = false;

    async function loadInitial() {
      setLoading(true);
      setError("");

      try {
        // ✅ When search filters are active, load ALL data (no limit)
        const hasSearchFilters =
          debouncedNameQ.trim() ||
          debouncedRecruiterQ.trim() ||
          debouncedLeaderQ.trim();

        const mapTrackerToRow = (tracker) => {
          const associate = tracker.associate || {};
          const fourByFour = tracker.fourByFour || {}; // ✅ Read 4x4 data
          const licensing = tracker.licensing || {}; // ✅ Read licensing data

          return {
            id: tracker.id,
            name: tracker.name || displayName(tracker),
            firstName: tracker.firstName,
            lastName: tracker.lastName,
            email: tracker.email,
            phone: tracker.phone,
            photoURL: tracker.photoURL,
            leader: tracker.leader || "—",
            recruiter: licensing.recruiter || "—",
            code: tracker.code,
            date: tracker.date,
            photo: tracker.photoURL,
            baseShopId: tracker.baseShopId || null,
            baseShop: tracker.baseShop || null,
            createdAt: tracker.createdAt || null,
            createdAtMillis: new Date(
              tracker.createdAt?.toDate?.() || tracker.createdAt
            ).getTime(),

            // Map associate fields to row properties (✅ read from 4x4 as fallback)
            multiHanded: associate.multiHanded || fourByFour.finish1stRecruit || false,
            tenThreeRulesGoals: associate.tenThreResultsGoals || fourByFour.personalSavings || false,
            selfImprovement: associate.selfImprovement || false,
            // NEW: Use milestone fields instead of legacy obs fields
            obsRecruitsDone: associate.milestones?.observe4Recruits || false,
            obsClientsDone: associate.milestones?.observe4Clients || false,
            getLicense: associate.getLicense || licensing.licensed || fourByFour.licensed || false, // ✅ Read from licensing too
            registerConvention: associate.registrationConvention || false,
            recruitsTtl: associate.recruitTtl || 0,
            personalPoints: associate.personalPoints || 0,
            licensesTtl: associate.licensesInTtl || 0,
            registrationsBase: associate.registrationsBase || 0,
            netLicense: associate.netLicense || false,
            netLicenseAmount: associate.netLicenseAmount || 0,
            keyPlayer: associate.keyPlayer || false,
            training: associate.training || false,
            bigEvent: associate.bigEvent || false,
            // Reset flags
            isEventReset: associate.isEventReset || false,
            isTrainingReset: associate.isTrainingReset || false,
            why: associate.why || "",
            goal: associate.goal || "",

            // ✅ Savings fields - Read from 4x4 directly
            savingsSecondDone: associate.savings?.secondDone || fourByFour.finish2ndSavings || false,
            savingsThirdDone: associate.savings?.thirdDone || fourByFour.finish3rdSavings || false,
            savingsFourthDone: associate.savings?.fourthDone || fourByFour.finish4thSavings || false,
          };
        };

        const applyRows = (nextRows, nextCursor) => {
          setRows(nextRows);

          nextRows.forEach((r) => {
            attachTrackerListener(r.id);
            attachLicensingListener(r.id); // ✅ Also attach licensing listener
          });

          pageStateRef.current.nextCursor = nextCursor || null;
        };

        if (hasSearchFilters) {
          const qy = query(
            collection(db, "trackers"),
            orderBy("associate.createdAt", "desc")
          );

          const snap = await getDocs(qy);
          if (cancelled) return;

          const trackerDocs = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            _doc: d,
          }));

          const baseRows = trackerDocs.map(mapTrackerToRow);
          applyRows(baseRows, null);
        } else {
          const pageSize = pageStateRef.current.pageSize;

          if (allowedUserIds.size === 0) {
            const qy = query(
              collection(db, "trackers"),
              orderBy("associate.createdAt", "desc"),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (cancelled) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const baseRows = trackerDocs.map(mapTrackerToRow);
            const last = snap.docs[snap.docs.length - 1] || null;
            applyRows(baseRows, last);
            return;
          }

          let cursor = null;
          let lastDoc = null;
          const seenIds = new Set();
          const collected = [];

          do {
            const qy = query(
              collection(db, "trackers"),
              orderBy("associate.createdAt", "desc"),
              ...(cursor ? [startAfter(cursor)] : []),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (cancelled) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const pageRows = trackerDocs.map(mapTrackerToRow);
            const allowedRows = pageRows.filter((r) =>
              allowedUserIds.has(r.id)
            );

            allowedRows.forEach((row) => {
              if (!seenIds.has(row.id)) {
                seenIds.add(row.id);
                collected.push(row);
              }
            });

            lastDoc = snap.docs[snap.docs.length - 1] || null;
            cursor = lastDoc;
          } while (collected.length < pageSize && lastDoc);

          applyRows(collected, lastDoc);
        }
      } catch (e) {
        console.error("Error loading trackers:", e);
        setError("Failed to load associate users.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    // Attach licensing listener to sync getLicense status
    function attachLicensingListener(userId) {
      if (licensingUnsubsRef.current.has(userId)) return;

      const unsub = onSnapshot(
        doc(db, "trackers", userId), // ✅ Fixed: Use trackers collection
        { includeMetadataChanges: false }, // Ignore metadata-only changes
        (docSnapshot) => {
          if (!docSnapshot.exists()) return;

          const trackerData = docSnapshot.data();
          const licensing = trackerData.licensing || {};

          // ✅ Sync getLicense from licensing.licensed field (new structure)
          const isLicensed = licensing.licensed || licensing.licenseActive || false;

          setRows((prev) =>
            prev.map((row) =>
              row.id === userId
                ? {
                  ...row,
                  getLicense: isLicensed,
                }
                : row
            )
          );
        }
      );

      licensingUnsubsRef.current.set(userId, unsub);
    }

    function attachTrackerListener(userId) {
      if (trackerUnsubsRef.current.has(userId)) return;

      // ✅ OPTIMIZATION: Only trigger on actual data changes (not metadata)
      const unsub = onSnapshot(
        trackersRef(userId),
        { includeMetadataChanges: false },
        (docSnapshot) => {
          if (!docSnapshot.exists()) return;

          const trackerData = docSnapshot.data();
          const associate = trackerData.associate || {};
          const fourByFour = trackerData.fourByFour || {}; // ✅ Read 4x4 data
          const licensing = trackerData.licensing || {}; // ✅ Read licensing data

          const updates = {
            // Root fields
            name: trackerData.name || displayName(trackerData),
            firstName: trackerData.firstName,
            lastName: trackerData.lastName,
            email: trackerData.email,
            phone: trackerData.phone,
            leader: trackerData.leader || "—",
            code: trackerData.code,
            date: trackerData.date,
            baseShop: trackerData.baseShop || null,

            // associate fields (these are mirrored FROM 4x4)
            recruiter: licensing.recruiter || "—",
            // ✅ Read from 4x4 directly for these fields
            multiHanded: associate.multiHanded || fourByFour.finish1stRecruit || false,
            tenThreeRulesGoals: associate.tenThreResultsGoals || fourByFour.personalSavings || false,
            selfImprovement: associate.selfImprovement || false,
            // NEW: Use milestone fields instead of legacy obs fields
            obsRecruitsDone: associate.milestones?.observe4Recruits || false,
            obsClientsDone: associate.milestones?.observe4Clients || false,
            getLicense: associate.getLicense || licensing.licensed || fourByFour.licensed || false, // ✅ Read from licensing too
            registerConvention: associate.registrationConvention || false,
            recruitsTtl: associate.recruitTtl || 0,
            personalPoints: associate.personalPoints || 0,
            licensesTtl: associate.licensesInTtl || 0,
            registrationsBase: associate.registrationsBase || 0,
            netLicense: associate.netLicense || false,
            netLicenseAmount: associate.netLicenseAmount || 0,
            keyPlayer: associate.keyPlayer || false,
            training: associate.training || false,
            bigEvent: associate.bigEvent || false,
            why: associate.why || "",
            goal: associate.goal || "",

            // ✅ Savings fields - Read from 4x4 directly
            savingsSecondDone: associate.savings?.secondDone || fourByFour.finish2ndSavings || false,
            savingsThirdDone: associate.savings?.thirdDone || fourByFour.finish3rdSavings || false,
            savingsFourthDone: associate.savings?.fourthDone || fourByFour.finish4thSavings || false,
          };

          setRows((prev) =>
            prev.map((row) => (row.id === userId ? { ...row, ...updates } : row))
          );
        });

      trackerUnsubsRef.current.set(userId, unsub);
    }

    loadInitial();

    return () => {
      cancelled = true;
      for (const [, unsub] of trackerUnsubsRef.current)
        try {
          unsub();
        } catch { }
      for (const [, unsub] of licensingUnsubsRef.current)
        try {
          unsub();
        } catch { }
      trackerUnsubsRef.current.clear();
      licensingUnsubsRef.current.clear();
      // ❌ DISABLED: cleanupWatchers();
    };
  }, [updateRowData, allowedUserIds, debouncedNameQ, debouncedRecruiterQ, debouncedLeaderQ]);

  // Auto-check Big Event when Register for Convention is checked
  useEffect(() => {
    rows.forEach((row) => {
      if (row.registerConvention && !row.bigEvent && !row.isEventReset) {
        // Auto-check Big Event when Register for Convention is checked
        persist(row.id, { bigEvent: true });
      }
    });
  }, [rows]);

  // Load more function
  const loadMore = useCallback(async () => {
    const cursor = pageStateRef.current.nextCursor;
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const qy = query(
        collection(db, "trackers"),
        orderBy("associate.createdAt", "desc"),
        startAfter(cursor),
        limit(pageStateRef.current.pageSize)
      );

      const snap = await getDocs(qy);
      const newTrackers = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        _doc: d,
      }));

      // ✅ Load ALL data - hierarchy filtering happens in filteredRows
      const moreRows = newTrackers
        .map((tracker) => {
        const associate = tracker.associate || {};
        const fourByFour = tracker.fourByFour || {}; // ✅ Read 4x4 data
        const licensing = tracker.licensing || {}; // ✅ Read licensing data

        return {
          id: tracker.id,
          name: tracker.name || displayName(tracker),
          firstName: tracker.firstName,
          lastName: tracker.lastName,
          email: tracker.email,
          phone: tracker.phone,
          photoURL: tracker.photoURL,
          leader: tracker.leader || "—",
          recruiter: associate.recruiter || "—",
          code: tracker.code,
          date: tracker.date,
          photo: tracker.photoURL,
          baseShopId: tracker.baseShopId || null,
          baseShop: tracker.baseShop || null,
          createdAt: tracker.createdAt || null,
          createdAtMillis: new Date(
            tracker.createdAt?.toDate?.() || tracker.createdAt
          ).getTime(),

          // Map associate fields (✅ read from 4x4 as fallback)
          multiHanded: associate.multiHanded || fourByFour.finish1stRecruit || false,
          tenThreeRulesGoals: associate.tenThreResultsGoals || fourByFour.personalSavings || false,
          selfImprovement: associate.selfImprovement || false,
          // NEW: Use milestone fields instead of legacy obs fields
          obsRecruitsDone: associate.milestones?.observe4Recruits || false,
          obsClientsDone: associate.milestones?.observe4Clients || false,
          getLicense: associate.getLicense || licensing.licensed || fourByFour.licensed || false, // ✅ Read from licensing too
          registerConvention: associate.registrationConvention || false,
          recruitsTtl: associate.recruitTtl || 0,
          personalPoints: associate.personalPoints || 0,
          licensesTtl: associate.licensesInTtl || 0,
          registrationsBase: associate.registrationsBase || 0,
          netLicense: associate.netLicense || false,
          netLicenseAmount: associate.netLicenseAmount || 0,
          keyPlayer: associate.keyPlayer || false,
          training: associate.training || false,
          bigEvent: associate.bigEvent || fourByFour.bigEvent || false,
          why: associate.why || "",
          goal: associate.goal || "",

          // ✅ Savings fields - Read from 4x4 directly
          savingsSecondDone: associate.savings?.secondDone || fourByFour.finish2ndSavings || false,
          savingsThirdDone: associate.savings?.thirdDone || fourByFour.finish3rdSavings || false,
          savingsFourthDone: associate.savings?.fourthDone || fourByFour.finish4thSavings || false,
        };
      });

      setRows((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const deduped = [...prev, ...moreRows.filter((r) => !ids.has(r.id))];
        // attach listeners for new ones
        moreRows.forEach((r) => {
          if (!trackerUnsubsRef.current.has(r.id)) {
            // ✅ OPTIMIZATION: Only trigger on actual data changes (not metadata)
            const unsub = onSnapshot(
              trackersRef(r.id),
              { includeMetadataChanges: false },
              (docSnapshot) => {
                if (!docSnapshot.exists()) return;

                const trackerData = docSnapshot.data();
                const associate = trackerData.associate || {};
                const fourByFour = trackerData.fourByFour || {}; // ✅ Read 4x4 data
                const licensing = trackerData.licensing || {}; // ✅ Read licensing data

                const updates = {
                  name: trackerData.name || displayName(trackerData),
                  firstName: trackerData.firstName,
                  lastName: trackerData.lastName,
                  email: trackerData.email,
                  phone: trackerData.phone,
                  leader: trackerData.leader || "—",
                  code: trackerData.code,
                  date: trackerData.date,
                  baseShop: trackerData.baseShop || null,
                  recruiter: associate.recruiter || "—",
                  // ✅ Read from 4x4 directly for these fields
                  multiHanded: associate.multiHanded || fourByFour.finish1stRecruit || false,
                  tenThreeRulesGoals: associate.tenThreResultsGoals || fourByFour.personalSavings || false,
                  selfImprovement: associate.selfImprovement || false,
                  // NEW: Use milestone fields instead of legacy obs fields
                  obsRecruitsDone:
                    associate.milestones?.observe4Recruits || false,
                  obsClientsDone: associate.milestones?.observe4Clients || false,
                  getLicense: associate.getLicense || licensing.licensed || fourByFour.licensed || false, // ✅ Read from licensing too
                  registerConvention: associate.registrationConvention || false,
                  recruitsTtl: associate.recruitTtl || 0,
                  personalPoints: associate.personalPoints || 0,
                  licensesTtl: associate.licensesInTtl || 0,
                  registrationsBase: associate.registrationsBase || 0,
                  netLicense: associate.netLicense || false,
                  netLicenseAmount: associate.netLicenseAmount || 0,
                  keyPlayer: associate.keyPlayer || false,
                  training: associate.training || false,
                  bigEvent: associate.bigEvent || fourByFour.bigEvent || false,
                  // Reset flags
                  isEventReset: associate.isEventReset || false,
                  isTrainingReset: associate.isTrainingReset || false,
                  why: associate.why || "",
                  goal: associate.goal || "",

                  // ✅ Savings fields - Read from 4x4 directly
                  savingsSecondDone: associate.savings?.secondDone || fourByFour.finish2ndSavings || false,
                  savingsThirdDone: associate.savings?.thirdDone || fourByFour.finish3rdSavings || false,
                  savingsFourthDone: associate.savings?.fourthDone || fourByFour.finish4thSavings || false,
                };

                setRows((p) =>
                  p.map((row) => (row.id === r.id ? { ...row, ...updates } : row))
                );
              });
            trackerUnsubsRef.current.set(r.id, unsub);
          }
          // Start data watchers for new rows
          // ❌ DISABLED: startWatchersForRow(r, updateRowData);
        });
        return deduped;
      });

      const last = snap.docs[snap.docs.length - 1] || null;
      pageStateRef.current.nextCursor = last || null;
    } catch (e) {
      console.error("Error loading more trackers:", e);
      setError("Failed to load more users.");
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, updateRowData, allowedUserIds]); // ✅ Only depend on allowedUserIds, not search filters

  useEffect(() => {
    if (!profileId) return setProfileData(null);
    const row = rows.find((r) => r.id === profileId);
    if (!row) return setProfileData(null);
    setProfileData({
      photoUrl: row.photoURL,
      name: row.name,
      email: row.email,
      phone: row.phone,
      recruiter: row.recruiter,
      leader: row.leader,
      amaDate: row.date,
      agencyCode: row.code,
      notes: row.why || row.goal,
    });
  }, [profileId, rows]);

  const updateRow = (rowId, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );

  // UPDATED: Field-level persist with sync and milestone recomputation
  const persist = async (rowId, patch) => {
    // Optimistic UI update - update only the specific field
    updateRow(rowId, patch);

    try {
      // Ensure associate exists only once per user session
      if (!initializedUsersRef.current.has(rowId)) {
        await ensureAssociateExists(
          rowId,
          rows.find((r) => r.id === rowId)
        );
        initializedUsersRef.current.add(rowId);
      }

      // For each field in patch, use field-level updates with sync
      for (const [uiKey, value] of Object.entries(patch)) {
        const associateFieldPath =
          UI_KEY_TO_ASSOCIATE_PATH[uiKey] || `associate.${uiKey}`;

        // Use the sync handler instead of direct update
        await onAssociateToggle(rowId, associateFieldPath, value);
      }
    } catch (e) {
      console.error("Error persisting update:", e);
      setError("Could not save. Try again.");
      // Revert optimistic update on error
      updateRow(
        rowId,
        Object.fromEntries(
          Object.keys(patch).map((key) => [
            key,
            rows.find((r) => r.id === rowId)?.[key],
          ])
        )
      );
    }
  };

  // Save Net License Amount
  const saveNetLicenseAmount = async (userId, amount) => {
    try {
      await persist(userId, { netLicenseAmount: amount });
    } catch (error) {
      console.error("Error saving net license amount:", error);
      setError("Failed to save amount.");
    }
  };

  // Notes functions for Why and Goal
  const addInlineNote = async (row, noteType) => {
    const draftKey = `${row.id}_${noteType}`;
    const text = (noteDrafts[draftKey] || "").trim();
    if (!text) return;

    try {
      const fieldKey = `${noteType}Notes`; // 'whyNotes' or 'goalNotes'
      const baseNotes = Array.isArray(row[fieldKey]) ? row[fieldKey] : [];
      const now = new Date();
      const nowIso = now.toISOString();
      const nowTimestamp = now.getTime();
      const userName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

      const newNote = {
        id: uniqueId(),
        text: text,
        createdAt: nowTimestamp,
        createdAtISO: nowIso,
        createdBy: userName,
      };

      const updatedNotes = [...baseNotes, newNote];

      // Update local state optimistically
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, [fieldKey]: updatedNotes } : r))
      );

      // Clear the draft
      setNoteDrafts((d) => ({ ...d, [draftKey]: "" }));

      // Save to Firebase
      await updateDoc(doc(db, "trackers", row.id), {
        [`associate.${fieldKey}`]: updatedNotes,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Error adding note:", e);
    }
  };

  const deleteNote = async (row, noteType, noteId) => {
    if (!row) return;
    setNoteDeletingId(noteId);

    try {
      const fieldKey = `${noteType}Notes`;
      const updatedNotes = (row[fieldKey] || []).filter((n) => n.id !== noteId);

      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, [fieldKey]: updatedNotes } : r
        )
      );

      await updateDoc(doc(db, "trackers", row.id), {
        [`associate.${fieldKey}`]: updatedNotes,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("Error deleting note:", e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  // Get current notes for modal
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor || !notesType) return [];
    const live = rows.find((r) => r.id === notesOpenFor.id) || notesOpenFor;
    const fieldKey = `${notesType}Notes`;
    return Array.isArray(live[fieldKey]) ? live[fieldKey] : [];
  }, [notesOpenFor, notesType, rows]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  // Reset Big Events for all users
  const handleResetEvents = async () => {
    if (
      window.confirm(
        "Reset Big Event for all users? This will only uncheck the Big Event column."
      )
    ) {
      try {
        setError("");
        // Reset Big Event for all filtered rows and set flag
        const resetPromises = filteredRows.map((row) =>
          persist(row.id, { bigEvent: false, isEventReset: true })
        );

        await Promise.all(resetPromises);
      } catch (error) {
        console.error("Error resetting events:", error);
        setError("Failed to reset events.");
      }
    }
  };

  const toggle = useCallback(
    (row, key) => persist(rid(row), { [key]: !row[key] }),
    [persist]
  );

  const setNumberField = useCallback(
    (row, key, value) =>
      persist(rid(row), { [key]: Math.max(0, Number(value ?? 0) || 0) }),
    [persist]
  );

  const setPoints = useCallback(
    (row, value) =>
      setNumberField(
        row,
        "personalPoints",
        Number(String(value).replace(/[^0-9]/g, "")) || 0
      ),
    [setNumberField]
  );

  // Click handlers for stat chips - open modal to show prospects
  const handleRecruitsClick = useCallback((row) => {
    const recruiterName = row.name || "";
    const recruiterId = row.id || "";
    setProspectsModal({
      open: true,
      filterType: 'allProspects', // Show ALL prospects added by this person
      filterValue: recruiterName,
      userName: recruiterName,
      userId: recruiterId,
      modalSource: 'recruits'
    });
  }, []);

  const handleProductionClick = useCallback((row) => {
    const userName = row.name || "";
    const userId = row.id || "";
    setProspectsModal({
      open: true,
      filterType: 'clientsOnly', // Show only prospects with outcome "Client" or "Both"
      filterValue: userName,
      userName: userName,
      userId: userId,
      modalSource: 'production'
    });
  }, []);

  const handleLicensesClick = useCallback((row) => {
    const userName = row.name || "";
    const userId = row.id || "";
    setProspectsModal({
      open: true,
      filterType: 'recruitsOnly', // Show only prospects with outcome "Recruit" or "Both"
      filterValue: userName,
      userName: userName,
      userId: userId,
      modalSource: 'licenses'
    });
  }, []);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    // Apply text filters (legacy and modern)
    const nq = debouncedNameQ.trim().toLowerCase();
    const rq = debouncedRecruiterQ.trim().toLowerCase();
    const lq = debouncedLeaderQ.trim().toLowerCase();
    const hasSearchFilters = !!(nq || rq || lq);

    // ✅ Apply hierarchy filter ONLY when no search filters are active
    if (!hasSearchFilters) {
      filtered = filtered.filter((r) => allowedUserIds.has(r.id));
    }

    // Apply search filters (shows ALL matching results, bypassing hierarchy)
    if (hasSearchFilters) {
      filtered = filtered.filter(
        (r) =>
          (nq ? (r.name || "").toLowerCase().includes(nq) : true) &&
          (rq ? (r.recruiter || "").toLowerCase().includes(rq) : true) &&
          (lq ? (r.leader || "").toLowerCase().includes(lq) : true)
      );
    }

    // Apply modern filters if enabled
    if (modernFiltersEnabled) {
      // Date filtering using 'date' field which is displayed in the UI
      filtered = filterByDateRange(
        filtered,
        dateFilter,
        customDateRange,
        "date" // Use date field for associate tracker (matches displayed date)
      );

      // BaseShop filtering using recruiter/leader fields
      filtered = filterByBaseShop(filtered, baseShopFilter, currentUser, {
        recruiterField: "recruiter",
        leaderField: "leader",
      });
    }

    // Sort the filtered results
    const va = (x) => (x?.toString?.() || "").toLowerCase();
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      const A = va(a[sortKey]);
      const B = va(b[sortKey]);
      const cmp = A.localeCompare(B);
      return sortDir === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [
    rows,
    debouncedNameQ,
    debouncedRecruiterQ,
    debouncedLeaderQ,
    sortKey,
    sortDir,
    modernFiltersEnabled,
    dateFilter,
    baseShopFilter,
    customDateRange,
    currentUser,
    allowedUserIds,
  ]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const MiniProfileBtn = ({ onClick }) => (
    <button
      type="button"
      className="profile-mini"
      title="Open profile"
      onClick={onClick}
    >
      👤
    </button>
  );

  const renderCell = (row, col) => {
    if (col.key === "name") {
      const sub = [formatDateShort(row.date), row.code]
        .filter(Boolean)
        .join(" • ");
      return (
        <div className="name-like-cell">
          <AvatarSquare
            row={row}
            size={34}
            onClick={() => openProfile(row.id)}
          />
          <div className="ndc-col">
            <div className="name-line">
              <span className="cell-text" title={row.name || "—"}>
                {row.name || "—"}
              </span>
            </div>
            {!!sub && <span className="ndc-sub">{sub}</span>}
          </div>
        </div>
      );
    }

    if (col.key === "recruiter" || col.key === "leader") {
      const text = row[col.key] || "—";
      return (
        <div className="cell-with-btn">
          <MiniProfileBtn onClick={() => openProfile(row.id)} />
          <span className="cell-text" title={text}>
            {text}
          </span>
        </div>
      );
    }

    if (col.type === "check") {
      const checked = !!row[col.key];
      const isReadOnly = !!col.readOnly;
      return (
        <div
          className={`light-cell ${checked ? "green" : "red"} ${isReadOnly ? "readonly" : ""
            }`}
          onClick={() => {
            if (isReadOnly) {
              showToast("This is not editable from associate tracker");
            } else {
              toggle(row, col.key);
            }
          }}
          role="button"
          tabIndex={isReadOnly ? -1 : 0}
          onKeyDown={(e) =>
            !isReadOnly &&
            (e.key === "Enter" || e.key === " ") &&
            toggle(row, col.key)
          }
          style={isReadOnly ? { cursor: "default", opacity: 0.9 } : {}}
        >
          <input
            type="checkbox"
            readOnly
            checked={checked}
            className="traffic-checkbox"
          />
        </div>
      );
    }

    if (col.type === "number") {
      return (
        <input
          type="number"
          min={0}
          className="cell-input center-txt"
          value={row[col.key] ?? 0}
          onChange={(e) => setNumberField(row, col.key, e.target.value)}
        />
      );
    }

    if (col.type === "pointsChip") {
      return (
        <button
          className="stat-chip"
          onClick={() => handleProductionClick(row)}
          title="Click to view production details"
        >
          {fmtInt(row.personalPoints)}
        </button>
      );
    }

    if (col.type === "statChip") {
      const value = row[col.key] ?? 0;
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Prevent event bubbling

        if (col.key === "recruitsTtl") {
          handleRecruitsClick(row);
        } else if (col.key === "licensesTtl") {
          handleLicensesClick(row);
        }
      };

      return (
        <button
          className="stat-chip"
          onClick={handleClick}
          title={`Click to view ${col.label.toLowerCase()}`}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >
          {fmtInt(value)}
        </button>
      );
    }

    if (col.type === "netLicensePopup") {
      const hasAmount = row && row.netLicenseAmount > 0;
      return (
        <button
          className={`btn ghost small fullw ${hasAmount ? "has-amount" : ""}`}
          onClick={() => setNetLicenseModal({ isOpen: true, row })}
          title={
            hasAmount ? `Edit amount: $${row.netLicenseAmount}` : "Enter amount"
          }
        >
          {hasAmount ? `$${fmtInt(row.netLicenseAmount)}` : "Enter $"}
        </button>
      );
    }

    if (col.type === "notesInline") {
      const noteType = col.key; // 'why' or 'goal'
      const fieldKey = `${noteType}Notes`; // 'whyNotes' or 'goalNotes'
      const draftKey = `${row.id}_${noteType}`;
      const rid = row.id;

      const notesArr = Array.isArray(row[fieldKey]) ? row[fieldKey] : [];
      const lastNote =
        notesArr.length > 0
          ? notesArr
            .slice()
            .reverse()
            .find((n) => n.text)
          : null;

      const isNoteFocused = focusedNoteInputId === draftKey;

      // Show latest note in the input when not focused, or draft when focused/typing
      const inputValue = isNoteFocused
        ? (noteDrafts[draftKey] || "")
        : (noteDrafts[draftKey] || (lastNote?.text || ""));

      return (
        <div className="notes-cell-content">
          <div className="notes-inline-add">
            <input
              className={`note-input ${!isNoteFocused && lastNote && !noteDrafts[draftKey] ? "showing-existing" : ""}`}
              type="text"
              placeholder={lastNote ? "Add a new note... (Press Enter)" : "Add a quick note... (Press Enter)"}
              value={inputValue}
              onChange={(e) =>
                setNoteDrafts((d) => ({ ...d, [draftKey]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && (noteDrafts[draftKey] || "").trim()) {
                  addInlineNote(row, noteType);
                }
              }}
              onFocus={() => {
                setFocusedNoteInputId(draftKey);
                // Clear the input when user focuses to type new note
                if (!noteDrafts[draftKey]) {
                  setNoteDrafts((d) => ({ ...d, [draftKey]: "" }));
                }
              }}
              onBlur={() => setFocusedNoteInputId(null)}
            />
            <button
              className="notes-openall-emoji"
              onClick={() => {
                setNotesOpenFor(row);
                setNotesType(noteType);
              }}
              title="Open all notes"
              type="button"
            >
              ✏️
            </button>
          </div>

          {/* Show note metadata below when showing existing note */}
          {!isNoteFocused && lastNote && !noteDrafts[draftKey] && (
            <div className="note-meta-under">
              {lastNote.createdBy || "—"} • {
                lastNote.createdAtISO
                  ? formatDateTimePretty(lastNote.createdAtISO)
                  : tsToMs(lastNote.createdAt)
                    ? formatDateTimePretty(
                      new Date(tsToMs(lastNote.createdAt)).toISOString()
                    )
                    : ""
              }
            </div>
          )}
        </div>
      );
    }

    return (
      <span className="cell-text" title={row[col.key] || "—"}>
        {row[col.key] || "—"}
      </span>
    );
  };

  // Calculate metrics for associate tracker
  const bigEvents = useMemo(() => {
    return filteredRows.filter((item) => item.bigEvent === true).length;
  }, [filteredRows]);

  const trainingCompleted = useMemo(() => {
    return filteredRows.filter((item) => item.training === true).length;
  }, [filteredRows]);

  const licenses = useMemo(() => {
    return filteredRows.filter((item) => item.getLicense === true).length;
  }, [filteredRows]);

  const netLicenses = useMemo(() => {
    return filteredRows.filter((item) => item.netLicenseAmount >= 1000).length;
  }, [filteredRows]);

  // Reset functions for associate tracker
  const handleResetTraining = async () => {
    if (window.confirm("Reset Training activities for all users?")) {
      try {
        setError("");
        // Reset Training for all filtered rows and set flag
        const resetPromises = filteredRows.map((row) =>
          persist(row.id, { training: false, isTrainingReset: true })
        );

        await Promise.all(resetPromises);
      } catch (error) {
        console.error("Error resetting training:", error);
        setError("Failed to reset training.");
      }
    }
  };

  // Create filters component for modern trackers
  const filtersComponent = modernTrackersEnabled ? (
    <div className="associate-tracker-filters">
      {/* Row 1: Reset buttons + Smaller Filters */}
      <div className="tracker-filter-row-1">
        <div className="reset-buttons-top">
          <button
            className="reset-btn"
            onClick={handleResetEvents}
            title="Reset Big Event for all users"
          >
            Reset Events
          </button>
          <button
            className="reset-btn"
            onClick={handleResetTraining}
            title="Reset Training activities for all users"
          >
            Reset Training
          </button>
        </div>
        <TrackerFilters
          users={rows}
          currentUser={currentUser}
          onRangeChange={(range) => {
            if (range) {
              setDateFilter("custom");
              setCustomDateRange(range);
            } else {
              setDateFilter("all");
              setCustomDateRange(null);
            }
          }}
          onBaseShopChange={setBaseShopFilter}
          defaultRange="all"
          defaultBaseShop="all"
          className="tracker-filter-controls-compact"
          layout="inline"
        />
      </div>

      {/* Row 2: All 5 metric cards in one row */}
      <div className="tracker-filter-row-2">
        <div className="metric-cards-all-row">
          <div className="tracker-metric-card-small">
            <div className="metric-icon">
              <IconUsers size={16} stroke={1.7} />
            </div>
            <div className="metric-copy">
              <span className="metric-label">People</span>
              <span className="metric-value">{filteredRows.length}</span>
            </div>
          </div>
          <div className="tracker-metric-card-small">
            <div className="metric-icon">
              <IconTarget size={16} stroke={1.7} />
            </div>
            <div className="metric-copy">
              <span className="metric-label">Big Events</span>
              <span className="metric-value">{bigEvents}</span>
            </div>
          </div>
          <div className="tracker-metric-card-small">
            <div className="metric-icon">
              <IconBook size={16} stroke={1.7} />
            </div>
            <div className="metric-copy">
              <span className="metric-label">Training</span>
              <span className="metric-value">{trainingCompleted}</span>
            </div>
          </div>
          <div className="tracker-metric-card-small">
            <div className="metric-icon">
              <IconLicense size={16} stroke={1.7} />
            </div>
            <div className="metric-copy">
              <span className="metric-label">Licenses</span>
              <span className="metric-value">{licenses}</span>
            </div>
          </div>
          <div className="tracker-metric-card-small">
            <div className="metric-icon">
              <IconCheck size={16} stroke={1.7} />
            </div>
            <div className="metric-copy">
              <span className="metric-label">Net Licenses</span>
              <span className="metric-value">{netLicenses}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  // Choose wrapper component based on UI version
  const TrackerWrapper = modernTrackersEnabled
    ? TrackerContainer
    : TrackerShell;

  const wrapperProps = modernTrackersEnabled
    ? {
      title: "Associate Tracker",
      left: error ? <span style={{ color: "#ff6b6b" }}>{error}</span> : null,
      trackerType: "associate",
      totalCount: null,
      showFilters: true,
      filterComponent: filtersComponent,
    }
    : {
      title: "Associate Tracker",
      left: error ? <span style={{ color: "#ff6b6b" }}>{error}</span> : null,
    };

  return (
    <>
      <TrackerWrapper {...wrapperProps}>
        {modernNotesEnabled && (
          <TrackerNotes
            userId={currentUser?.uid}
            trackerType="associate"
            className="associate-notes"
          />
        )}

        <ResizableGrid
          columns={COLS}
          groups={GROUPS}
          rows={filteredRows}
          loading={loading}
          emptyText="No associates found."
          rowKey={(r) => r.id}
          lockableLeftCount={3}
          initialLockedKeys={["name", "recruiter", "leader"]}
          renderHeaderSearch={(col) => {
            if (col.key === "name")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Name…"
                  value={nameQ}
                  onChange={(e) => setNameQ(e.target.value)}
                />
              );
            if (col.key === "recruiter")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Recruiter…"
                  value={recruiterQ}
                  onChange={(e) => setRecruiterQ(e.target.value)}
                />
              );
            if (col.key === "leader")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Leader…"
                  value={leaderQ}
                  onChange={(e) => setLeaderQ(e.target.value)}
                />
              );
            return null;
          }}
          renderCell={renderCell}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={onSort}
          loadingMore={loadingMore}
        />
        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <button
            className="btn"
            onClick={loadMore}
            disabled={loadingMore || !pageStateRef.current.nextCursor}
          >
            {pageStateRef.current.nextCursor
              ? loadingMore
                ? "Loading…"
                : "Load more users"
              : "All loaded"}
          </button>
        </div>
      </TrackerWrapper>

      <UserProfileCard
        open={!!profileId && !!profileData}
        onClose={closeProfile}
        person={profileData || {}}
      />

      {/* Net License Amount Modal */}
      <NetLicenseModal
        isOpen={netLicenseModal.isOpen}
        onClose={() => setNetLicenseModal({ isOpen: false, row: null })}
        row={netLicenseModal.row}
        onSave={saveNetLicenseAmount}
      />

      {/* Why/Goal Notes Modal - ProspectTracker style */}
      {notesOpenFor && notesType && (
        <div className="modal-backdrop-notes" onClick={() => { setNotesOpenFor(null); setNotesType(null); }}>
          <div
            className="modal-card-notes"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head-notes">
              <h3>
                {notesType === 'why' ? 'Why' : 'Goal'} Notes — {notesOpenFor.name || "User"}
              </h3>
              <button
                className="btn-notes ghost"
                onClick={() => { setNotesOpenFor(null); setNotesType(null); }}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal-notes">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal-notes">
                  <div className="note-text-modal-notes">{n.text}</div>
                  <div className="note-meta-modal-notes">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : tsToMs(n.createdAt)
                        ? formatDateTimeISO(
                          new Date(tsToMs(n.createdAt)).toISOString()
                        )
                        : ""}
                  </div>
                  <button
                    className="note-delete-modal-notes"
                    onClick={() => deleteNote(notesOpenFor, notesType, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add-notes">
              <textarea
                className="input-notes"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn-notes primary"
                onClick={async () => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (!text) return;

                  const fieldKey = `${notesType}Notes`;
                  const baseNotes = Array.isArray(notesOpenFor[fieldKey]) ? notesOpenFor[fieldKey] : [];
                  const now = new Date();
                  const nowIso = now.toISOString();
                  const nowTimestamp = now.getTime();
                  const userName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

                  const newNote = {
                    id: uniqueId(),
                    text: text,
                    createdAt: nowTimestamp,
                    createdAtISO: nowIso,
                    createdBy: userName,
                  };

                  const updatedNotes = [...baseNotes, newNote];

                  setRows((prev) =>
                    prev.map((r) =>
                      r.id === notesOpenFor.id
                        ? { ...r, [fieldKey]: updatedNotes }
                        : r
                    )
                  );

                  setNoteDrafts((d) => ({ ...d, modal: "" }));

                  try {
                    await updateDoc(doc(db, "trackers", notesOpenFor.id), {
                      [`associate.${fieldKey}`]: updatedNotes,
                      updatedAt: serverTimestamp(),
                    });
                  } catch (e) {
                    console.error("Error updating notes:", e);
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drill-down Modals - REMOVED: Now using navigation to ProspectTracker */}

      {/* Notes Modal - only show if modern notes enabled */}
      {modernNotesEnabled && showNotesModal && (
        <div
          className="notes-modal-backdrop"
          onClick={() => setShowNotesModal(false)}
        >
          <div
            className="notes-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="notes-modal-header">
              <h3>Associate Tracker Notes</h3>
              <button
                className="notes-modal-close"
                onClick={() => setShowNotesModal(false)}
              >
                ×
              </button>
            </div>
            <div className="notes-modal-body">
              <TrackerNotes
                userId={currentUser?.uid}
                trackerType="associate"
                className="modal-notes"
              />
            </div>
          </div>
        </div>
      )}

      <style>{`
        /* Hide horizontal scrollbar but keep scrolling functionality */
        .rg-wrap .rg-locked-section {
          overflow-x: auto;
          scrollbar-width: thin; /* Firefox */
        }

        .rg-wrap .rg-locked-section::-webkit-scrollbar {
          width: 8px;
          height: 0px; /* Hide horizontal scrollbar */
        }

        .rg-wrap .rg-locked-section::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .rg-wrap .rg-locked-section::-webkit-scrollbar-thumb {
          background: rgba(255, 215, 0, 0.3);
          border-radius: 4px;
        }

        .rg-wrap .rg-scroll {
          overflow-x: auto;
          overflow-y: auto;
          scrollbar-width: thin; /* Firefox */
        }

        .rg-wrap .rg-scroll::-webkit-scrollbar {
          width: 8px;
          height: 0px; /* Hide horizontal scrollbar */
        }

        .rg-wrap .rg-scroll::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
        }

        .rg-wrap .rg-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 215, 0, 0.3);
          border-radius: 4px;
        }

        /* Associate Tracker Layout Styles - 3+2 Grid */
        .associate-tracker-filters {
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .tracker-filter-row-1,
        .tracker-filter-row-2 {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .metric-cards-group {
          display: flex;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
        }

        .tracker-metric-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 18px;
          border-radius: 16px;
          background: linear-gradient(135deg, rgba(56, 45, 18, 0.92), rgba(34, 26, 10, 0.92));
          border: 1px solid rgba(243, 214, 122, 0.45);
          box-shadow: 0 12px 28px rgba(22, 17, 6, 0.35);
          color: #fdf7dc;
        }

        .tracker-metric-card .metric-icon {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: rgba(243, 214, 122, 0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f3d67a;
        }

        .tracker-metric-card .metric-copy {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
        }

        .tracker-metric-card .metric-label {
          font-size: 11px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(253, 244, 210, 0.75);
        }

        .tracker-metric-card .metric-value {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.02em;
          color: #ffe59a;
        }

        .tracker-filter-controls-two-col.tracker-filters {
          flex: 1;
          justify-content: flex-end;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .reset-buttons-inline {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .reset-btn {
          background: rgba(239, 83, 80, 0.15);
          border: 1px solid rgba(239, 83, 80, 0.4);
          color: #ef5350;
          border-radius: 8px;
          padding: 8px 16px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .reset-btn:hover {
          background: rgba(239, 83, 80, 0.25);
          border-color: rgba(239, 83, 80, 0.6);
          color: #ff6b6b;
        }

        /* Net License Modal Styles */
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        
        .modal-content {
          background: #1a1a1f;
          border-radius: 12px;
          width: 90%;
          max-width: 400px;
          max-height: 80vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          border: 1px solid #333;
        }
        
        .modal-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #333;
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #252830;
        }
        
        .modal-header h3 {
          margin: 0;
          color: #fff;
          font-size: 18px;
          font-weight: 600;
        }
        
        .modal-close {
          background: none;
          border: none;
          color: #999;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        
        .modal-close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        
        .modal-body {
          padding: 20px 24px;
        }

        .modal-footer {
          padding: 16px 24px 20px;
          border-top: 1px solid #333;
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .input-group label {
          color: #fff;
          font-weight: 500;
          margin-bottom: 4px;
        }

        .amount-input-container {
          position: relative;
          display: flex;
          align-items: center;
        }

        .currency-symbol {
          position: absolute;
          left: 12px;
          color: #999;
          font-weight: 500;
          z-index: 1;
        }

        .amount-input {
          width: 100%;
          padding: 12px 12px 12px 24px;
          background: #2a2a2f;
          border: 1px solid #444;
          border-radius: 8px;
          color: #fff;
          font-size: 16px;
        }

        .amount-input:focus {
          outline: none;
          border-color: #f3d67a;
        }

        .btn.primary {
          background: #f3d67a;
          color: #1a1a1f;
          border: none;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn.secondary {
          background: #444;
          color: #fff;
          border: 1px solid #666;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 500;
          cursor: pointer;
        }

        .btn.primary:hover {
          background: #ffe59a;
        }

        .btn.secondary:hover {
          background: #555;
        }

        /* Net License button styles */
        .btn.ghost.small.fullw.has-amount {
          background: rgba(243, 214, 122, 0.15);
          border-color: rgba(243, 214, 122, 0.4);
          color: #f3d67a;
        }

        @media (max-width: 1200px) {
          .associate-tracker-filters {
            gap: 8px;
          }
          
          .tracker-filter-row-1,
          .tracker-filter-row-2 {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .metric-cards-group {
            justify-content: flex-start;
            width: 100%;
          }

          .tracker-filter-controls-two-col.tracker-filters {
            grid-template-columns: 1fr;
            justify-content: flex-start;
          }

          .reset-buttons-inline {
            justify-content: flex-start;
            width: 100%;
          }
        }

        @media (max-width: 768px) {
          .metric-cards-group {
            flex-direction: column;
            align-items: stretch;
          }

          .tracker-metric-card {
            width: 100%;
          }

          .reset-buttons-inline {
            flex-direction: column;
          }

          .reset-btn {
            width: 100%;
            text-align: center;
          }

          .modal-footer {
            flex-direction: column;
          }
        }
        
        /* Notes Modal Styles for Associate Tracker */
        .notes-modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        
        .notes-modal-content {
          background: #1a1a1f;
          border-radius: 12px;
          width: 90%;
          max-width: 600px;
          max-height: 80vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
          border: 1px solid #333;
        }
        
        .notes-modal-header {
          padding: 20px 24px 16px;
          border-bottom: 1px solid #333;
          display: flex;
          align-items: center;
          justify-content: between;
          background: #252830;
        }
        
        .notes-modal-header h3 {
          margin: 0;
          color: #fff;
          font-size: 18px;
          font-weight: 600;
        }
        
        .notes-modal-close {
          background: none;
          border: none;
          color: #999;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
        }
        
        .notes-modal-close:hover {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
        }
        
        .notes-modal-body {
          padding: 20px 24px;
          max-height: 60vh;
          overflow-y: auto;
        }

        /* New Compact Layout Styles */
        .reset-buttons-top {
          display: flex;
          gap: 12px;
          justify-content: flex-start;
        }

        .tracker-filter-controls-compact {
          width: 70%; /* 30% reduction from default */
          flex-shrink: 0;
        }

        .metric-cards-all-row {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          justify-content: flex-start;
          width: 100%;
        }

        .tracker-metric-card-small {
          display: flex;
          align-items: center;
          gap: 8px; /* reduced from 12px */
          padding: 8px 12px; /* reduced from 12px 18px */
          border-radius: 12px; /* slightly smaller than 16px */
          background: linear-gradient(135deg, rgba(56, 45, 18, 0.92), rgba(34, 26, 10, 0.92));
          border: 1px solid rgba(243, 214, 122, 0.45);
          box-shadow: 0 8px 20px rgba(22, 17, 6, 0.25); /* lighter shadow */
          color: #fdf7dc;
          flex: 1;
          min-width: 140px; /* ensures reasonable minimum width */
          max-width: 180px; /* prevents cards from getting too wide */
        }

        .tracker-metric-card-small .metric-icon {
          width: 30px; /* reduced from 40px */
          height: 30px; /* reduced from 40px */
          border-radius: 8px; /* reduced from 12px */
          background: rgba(243, 214, 122, 0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #f3d67a;
        }

        .tracker-metric-card-small .metric-copy {
          display: flex;
          flex-direction: column;
          line-height: 1.1; /* tighter line height */
        }

        .tracker-metric-card-small .metric-label {
          font-size: 10px; /* reduced from 11px */
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(253, 244, 210, 0.75);
        }

        .tracker-metric-card-small .metric-value {
          font-size: 16px; /* reduced from 22px */
          font-weight: 700;
          letter-spacing: 0.02em;
          color: #ffe59a;
        }

        /* Responsive adjustments for small cards */
        @media (max-width: 1400px) {
          .metric-cards-all-row {
            gap: 8px;
          }

          .tracker-metric-card-small {
            min-width: 120px;
            max-width: 160px;
          }
        }

        @media (max-width: 1200px) {
          .tracker-filter-controls-compact {
            width: 100%;
          }

          .reset-buttons-top {
            justify-content: flex-start;
            width: 100%;
            margin-bottom: 8px;
          }

          .metric-cards-all-row {
            flex-wrap: wrap;
            justify-content: space-between;
          }

          .tracker-metric-card-small {
            flex: 1 1 calc(50% - 6px);
            min-width: 140px;
          }
        }

        @media (max-width: 768px) {
          .metric-cards-all-row {
            flex-direction: column;
          }

          .tracker-metric-card-small {
            flex: 1 1 100%;
            max-width: none;
          }
        }

        /* Prospects Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          animation: fadeIn 0.2s ease;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .prospects-modal {
          background: linear-gradient(135deg, #1a1d29 0%, #252830 100%);
          border-radius: 16px;
          width: 90%;
          max-width: 900px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
          border: 1px solid rgba(255, 255, 255, 0.1);
          animation: slideUp 0.3s ease;
        }

        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .modal-header {
          padding: 24px 28px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: rgba(255, 255, 255, 0.03);
        }

        .modal-header h2 {
          margin: 0;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
        }

        .modal-close-btn {
          background: rgba(239, 83, 80, 0.15);
          border: 1px solid rgba(239, 83, 80, 0.3);
          color: #ef5350;
          font-size: 28px;
          width: 40px;
          height: 40px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          font-weight: 300;
          line-height: 1;
        }

        .modal-close-btn:hover {
          background: rgba(239, 83, 80, 0.25);
          border-color: rgba(239, 83, 80, 0.5);
          transform: scale(1.05);
        }

        .modal-body {
          padding: 0;
          overflow-y: auto;
          flex: 1;
        }

        .loading-state,
        .empty-state {
          padding: 48px 28px;
          text-align: center;
          color: #999;
          font-size: 16px;
        }

        .prospects-list {
          padding: 0;
        }

        .prospects-table {
          width: 100%;
          border-collapse: collapse;
        }

        .prospects-table thead th {
          background: rgba(255, 255, 255, 0.05);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #999;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .prospects-table tbody tr {
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
          transition: background 0.2s ease;
        }

        .prospects-table tbody tr:hover {
          background: rgba(255, 255, 255, 0.03);
        }

        .prospects-table tbody td {
          padding: 16px;
          color: #e0e0e0;
          font-size: 14px;
        }

        .outcome-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .outcome-badge.booked {
          background: rgba(76, 175, 80, 0.2);
          color: #4caf50;
          border: 1px solid rgba(76, 175, 80, 0.4);
        }

        .outcome-badge.interested {
          background: rgba(33, 150, 243, 0.2);
          color: #2196f3;
          border: 1px solid rgba(33, 150, 243, 0.4);
        }

        .outcome-badge.notinterested {
          background: rgba(239, 83, 80, 0.2);
          color: #ef5350;
          border: 1px solid rgba(239, 83, 80, 0.4);
        }

        .icon-badge {
          font-size: 18px;
        }

        .icon-badge.active {
          filter: drop-shadow(0 0 4px currentColor);
        }

        .modal-footer {
          padding: 20px 28px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: flex-end;
          background: rgba(255, 255, 255, 0.03);
        }

        .btn {
          padding: 10px 24px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
        }

        .btn.secondary {
          background: rgba(255, 255, 255, 0.1);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn.secondary:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }

        /* Prospect Table Modal - Black/Gold/White Theme */
        .prospect-table-modal-associate {
          background: #1a1a1a;
          border-radius: 12px;
          border: 2px solid #d4af37;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(212, 175, 55, 0.3);
          transition: all 0.3s ease;
          position: fixed;
          top: 50%;
          transform: translateY(-50%);
        }

        .prospect-modal-header-associate {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 30px;
          border-bottom: 2px solid #d4af37;
          background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
        }

        .prospect-modal-header-associate h2 {
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          color: #d4af37;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        }

        .prospect-modal-close-associate {
          background: #d4af37;
          color: #1a1a1a;
          border: none;
          font-size: 24px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          cursor: pointer;
          font-weight: bold;
          transition: all 0.2s ease;
        }

        .prospect-modal-close-associate:hover {
          background: #f0c849;
          transform: scale(1.1);
          box-shadow: 0 4px 12px rgba(212, 175, 55, 0.4);
        }

        .prospect-table-container-associate {
          flex: 1;
          overflow: auto;
          padding: 20px;
          background: #1a1a1a;
        }

        /* Custom scrollbar for dark theme */
        .prospect-table-container-associate::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }

        .prospect-table-container-associate::-webkit-scrollbar-track {
          background: #0a0a0a;
          border-radius: 6px;
        }

        .prospect-table-container-associate::-webkit-scrollbar-thumb {
          background: #d4af37;
          border-radius: 6px;
        }

        .prospect-table-container-associate::-webkit-scrollbar-thumb:hover {
          background: #f0c849;
        }

        .prospect-table-associate {
          width: 100%;
          border-collapse: collapse;
          min-width: 2000px;
        }

        .prospect-table-associate thead {
          position: sticky;
          top: 0;
          background: #2a2a2a;
          z-index: 10;
          box-shadow: 0 2px 8px rgba(212, 175, 55, 0.2);
        }

        .prospect-table-associate th {
          padding: 14px 16px;
          text-align: left;
          font-weight: 700;
          font-size: 13px;
          color: #d4af37;
          border-bottom: 2px solid #d4af37;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: #2a2a2a;
        }

        /* Google Sheets Style Headers */
        .prospect-table-associate th.resizable-header-associate {
          padding: 0;
          text-align: left;
          font-weight: 700;
          font-size: 13px;
          color: #d4af37;
          border-right: 1px solid #444;
          border-bottom: 2px solid #d4af37;
          white-space: nowrap;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          background: #2a2a2a;
          position: relative;
          height: 48px;
        }

        .prospect-table-associate th.resizable-header-associate:last-child {
          border-right: none;
        }

        .header-content-associate {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 12px;
          height: 100%;
        }

        .header-label-associate {
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sort-button-associate {
          background: transparent;
          border: none;
          color: #d4af37;
          cursor: pointer;
          padding: 4px 8px;
          font-size: 12px;
          margin-left: 8px;
          border-radius: 4px;
          transition: all 0.2s ease;
          opacity: 0.7;
        }

        .sort-button-associate:hover {
          background: rgba(212, 175, 55, 0.2);
          opacity: 1;
        }

        .column-resizer-associate {
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          cursor: col-resize;
          background: transparent;
          transition: background 0.2s ease;
        }

        .column-resizer-associate:hover {
          background: #d4af37;
        }

        .prospect-table-associate td {
          padding: 12px 16px;
          border-bottom: 1px solid #333;
          border-right: 1px solid #2a2a2a;
          font-size: 14px;
          color: #ffffff;
        }

        .prospect-table-associate td:last-child {
          border-right: none;
        }

        .prospect-table-associate.google-sheets-style td {
          border-right: 1px solid #444;
        }

        .prospect-table-associate.google-sheets-style td:last-child {
          border-right: none;
        }

        .prospect-table-associate tbody tr {
          background: #1a1a1a;
          transition: background 0.2s ease;
        }

        .prospect-table-associate tbody tr:hover {
          background: #2a2a2a;
          box-shadow: 0 2px 8px rgba(212, 175, 55, 0.1);
        }

        .prospect-table-associate tbody tr:nth-child(even) {
          background: #1f1f1f;
        }

        .prospect-table-associate tbody tr:nth-child(even):hover {
          background: #2a2a2a;
        }

        .empty-state-associate {
          text-align: center !important;
          padding: 60px 20px !important;
          color: #d4af37;
          font-style: italic;
          font-size: 16px;
        }

        .center-cell {
          text-align: center;
        }

        .badge-check {
          color: #4caf50;
          font-size: 18px;
          font-weight: bold;
        }

        .badge-hot {
          font-size: 18px;
        }

        .outcome-badge-associate {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .outcome-badge-associate.client {
          background: rgba(76, 175, 80, 0.2);
          color: #4caf50;
          border: 1px solid #4caf50;
        }

        .outcome-badge-associate.recruit {
          background: rgba(33, 150, 243, 0.2);
          color: #2196f3;
          border: 1px solid #2196f3;
        }

        .outcome-badge-associate.both {
          background: rgba(212, 175, 55, 0.2);
          color: #d4af37;
          border: 1px solid #d4af37;
        }

        .profile-cell,
        .notes-cell {
          max-width: 520px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .action-btn-associate {
          background: #d4af37;
          color: #1a1a1a;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          transition: all 0.2s ease;
        }

        .action-btn-associate:hover {
          background: #f0c849;
          transform: scale(1.05);
        }

        /* Notes Cell Styles for Why/Goal */
        .notes-cell-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
          align-items: flex-start;
        }

        .notes-inline-add {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .note-input {
          flex: 1 1 auto;
          min-width: 120px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .note-input.showing-existing {
          background: rgba(255, 215, 0, 0.08);
          border-left: 3px solid rgba(255, 215, 0, 0.4);
          padding-left: 8px;
          font-style: italic;
          opacity: 0.95;
        }

        .notes-openall-emoji {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 8px;
          cursor: pointer;
        }

        .note-meta-under {
          font-size: 11px;
          opacity: 0.8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Notes Modal Styles - ProspectTracker style (with -notes suffix to avoid conflicts) */
        .modal-backdrop-notes {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
        }
        
        .modal-card-notes {
          background: #1f2128;
          color: #fff;
          border: 1px solid #3a3f4a;
          border-radius: 16px;
          box-shadow: 0 14px 40px rgba(0,0,0,.45);
          padding: 16px 16px 12px;
          max-height: 92vh;
          overflow: auto;
          width: min(860px,94vw);
        }
        
        .modal-head-notes {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .btn-notes {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.18);
          color: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
        }
        
        .btn-notes.primary {
          background: rgba(255,215,0,.2);
          color: #ffd700;
          border-color: rgba(255,215,0,.35);
        }
        
        .btn-notes.ghost {
          background: transparent;
          border-color: rgba(255,255,255,.25);
        }
        
        .input-notes {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,.06);
          color: #fff;
          border: 1px solid rgba(255,255,255,.15);
          border-radius: 10px;
          padding: 10px 12px;
          outline: none;
        }
        
        .input-notes:focus {
          border-color: rgba(255,215,0,.55);
          box-shadow: 0 0 0 2px rgba(255,215,0,.12) inset;
        }
        
        .notes-list-modal-notes {
          max-height: 40vh;
          overflow: auto;
          background: rgba(255,255,255,.04);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 12px;
          padding: 8px;
          margin-bottom: 12px;
        }
        
        .empty-notes-notes {
          opacity: .7;
          text-align: center;
          padding: 18px 8px;
        }
        
        .note-row-modal-notes {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          border-radius: 10px;
        }
        
        .note-row-modal-notes:not(:last-child) {
          border-bottom: 1px dashed rgba(255,255,255,.08);
        }
        
        .note-text-modal-notes {
          white-space: pre-wrap;
        }
        
        .note-meta-modal-notes {
          opacity: .8;
          font-size: 12px;
        }
        
        .note-delete-modal-notes {
          background: transparent;
          border: 1px dashed rgba(255,100,100,.4);
          color: rgba(255,100,100,.9);
          border-radius: 8px;
          padding: 4px 8px;
          cursor: pointer;
        }
        
        .notes-modal-add-notes {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* ✅ OPTIMIZED GLASS MODAL STYLES */
        .modal-overlay-glass {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          animation: fadeIn 0.15s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .prospects-modal-glass {
          width: 90%;
          max-width: 1200px;
          max-height: 85vh;
          background: linear-gradient(135deg, rgba(18, 18, 18, 0.95) 0%, rgba(35, 35, 35, 0.9) 100%);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 215, 0, 0.1);
          backdrop-filter: blur(20px);
          display: flex;
          flex-direction: column;
          animation: slideUp 0.2s ease;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .prospects-modal-header-glass {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          border-bottom: 1px solid rgba(255, 215, 0, 0.2);
          background: linear-gradient(to bottom, rgba(255, 215, 0, 0.08), transparent);
        }

        .prospects-modal-header-glass h2 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: #ffd700;
          text-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
        }

        .prospects-modal-close-glass {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #fff;
          font-size: 24px;
          width: 36px;
          height: 36px;
          border-radius: 8px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .prospects-modal-close-glass:hover {
          background: rgba(255, 59, 59, 0.8);
          border-color: rgba(255, 59, 59, 1);
          transform: scale(1.05);
        }

        .prospects-modal-search {
          padding: 16px 24px;
          border-bottom: 1px solid rgba(255, 215, 0, 0.1);
        }

        .prospects-search-input-glass {
          width: 100%;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 8px;
          color: #fff;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
        }

        .prospects-search-input-glass:focus {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 215, 0, 0.6);
          box-shadow: 0 0 0 3px rgba(255, 215, 0, 0.1);
        }

        .prospects-search-input-glass::placeholder {
          color: rgba(255, 255, 255, 0.4);
        }

        .prospects-table-wrapper-glass {
          flex: 1;
          overflow: auto;
          padding: 0 24px;
        }

        .prospects-table-glass {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
        }

        .prospects-table-glass thead {
          position: sticky;
          top: 0;
          z-index: 10;
          background: linear-gradient(to bottom, rgba(18, 18, 18, 0.98), rgba(25, 25, 25, 0.95));
        }

        .prospects-table-glass th {
          padding: 14px 12px;
          text-align: left;
          font-size: 13px;
          font-weight: 600;
          color: #ffd700;
          border-bottom: 2px solid rgba(255, 215, 0, 0.3);
          cursor: pointer;
          user-select: none;
          transition: background 0.2s;
          white-space: nowrap;
        }

        .prospects-table-glass th:hover {
          background: rgba(255, 215, 0, 0.1);
        }

        .prospects-table-glass tbody tr {
          transition: background 0.15s;
        }

        .prospects-table-glass tbody tr:hover {
          background: rgba(255, 215, 0, 0.06);
        }

        .prospects-table-glass td {
          padding: 12px;
          font-size: 13px;
          color: rgba(255, 255, 255, 0.9);
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .status-badge-glass {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge-glass.new {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.4);
        }

        .status-badge-glass.contacted {
          background: rgba(168, 85, 247, 0.2);
          color: #c084fc;
          border: 1px solid rgba(168, 85, 247, 0.4);
        }

        .status-badge-glass.interested {
          background: rgba(34, 197, 94, 0.2);
          color: #4ade80;
          border: 1px solid rgba(34, 197, 94, 0.4);
        }

        .status-badge-glass.invited {
          background: rgba(255, 215, 0, 0.2);
          color: #ffd700;
          border: 1px solid rgba(255, 215, 0, 0.4);
        }

        .prospects-loading-glass,
        .prospects-empty-glass {
          padding: 60px 20px;
          text-align: center;
          color: rgba(255, 255, 255, 0.5);
          font-size: 14px;
        }

        .prospects-modal-footer-glass {
          padding: 16px 24px;
          border-top: 1px solid rgba(255, 215, 0, 0.1);
          text-align: center;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          background: linear-gradient(to top, rgba(255, 215, 0, 0.05), transparent);
        }

        /* Scrollbar styling */
        .prospects-table-wrapper-glass::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        .prospects-table-wrapper-glass::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
        }

        .prospects-table-wrapper-glass::-webkit-scrollbar-thumb {
          background: rgba(255, 215, 0, 0.3);
          border-radius: 4px;
        }

        .prospects-table-wrapper-glass::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 215, 0, 0.5);
        }

        /* ✅ PROSPECT TRACKER COLUMN STYLES */
        .prospect-marker-glass {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          margin: 0 auto;
          border: 2px solid rgba(255, 255, 255, 0.3);
        }

        .check-icon-glass {
          color: #4ade80;
          font-weight: bold;
          font-size: 16px;
        }

        .outcome-badge-glass {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          text-transform: capitalize;
        }

        .outcome-badge-glass.client {
          background: rgba(34, 197, 94, 0.2);
          color: #4ade80;
          border: 1px solid rgba(34, 197, 94, 0.4);
        }

        .outcome-badge-glass.recruit {
          background: rgba(59, 130, 246, 0.2);
          color: #60a5fa;
          border: 1px solid rgba(59, 130, 246, 0.4);
        }

        .outcome-badge-glass.both {
          background: rgba(255, 215, 0, 0.2);
          color: #ffd700;
          border: 1px solid rgba(255, 215, 0, 0.4);
        }

        .profile-line-glass {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .profile-line-glass span {
          background: rgba(255, 255, 255, 0.05);
          padding: 2px 8px;
          border-radius: 6px;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.8);
        }

        .action-btn-glass {
          display: inline-block;
          padding: 6px 10px;
          margin-right: 6px;
          background: rgba(255, 215, 0, 0.1);
          border: 1px solid rgba(255, 215, 0, 0.3);
          border-radius: 6px;
          text-decoration: none;
          font-size: 14px;
          transition: all 0.2s;
        }

        .action-btn-glass:hover {
          background: rgba(255, 215, 0, 0.2);
          border-color: rgba(255, 215, 0, 0.6);
          transform: scale(1.05);
        }

        /* Horizontal scroll */
        .prospects-table-glass {
          min-width: 2200px; /* Sum of all column widths */
        }
      `}</style>

      {/* Prospects Modal */}
      <ProspectsModal
        isOpen={prospectsModal.open}
        onClose={() => setProspectsModal({ open: false, filterType: null, filterValue: '', userName: '', userId: '', modalSource: '' })}
        filterType={prospectsModal.filterType}
        filterValue={prospectsModal.filterValue}
        userName={prospectsModal.userName}
        userId={prospectsModal.userId}
        modalSource={prospectsModal.modalSource}
      />
    </>
  );
}
